// Brivo Mobile App — splash, onboarding, login, bottom-nav shell, 8 modules
// Routes mounted at /m/* in App.js
import { useEffect, useState, createContext, useContext, useMemo, useRef } from "react";
import { Routes, Route, useNavigate, useLocation, Link, Navigate, Outlet, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Home, ShoppingBag, Package, ShoppingCart, FileText, Wallet, Calendar, CreditCard, BarChart3,
  ChevronRight, ArrowRight, ArrowLeft, LogOut, Bell, Lock, Plus, AlertCircle, X, Menu, Eye, EyeOff,
  TrendingUp, Activity, Search, Save, Trash2, Check, MinusCircle, PlusCircle, Settings, Upload,
  Image as ImageIcon, Film, Store, Heart, MessageCircle, Share2, Volume2, VolumeX, Bookmark, Tag,
  Sparkles, Flame, Send, Play, Pause, Video, Edit3, Circle, Square, Flag, AlertCircle as AlertIcon,
} from "lucide-react";
import axios from "axios";
import { API, getToken, getUser, getTenant, setSession, clearSession } from "../../lib/auth";
import { CurrencyProvider, useCurrency } from "../../lib/currency";
import { FullPageLoader } from "../../components/PageLoader";

// ───────────────────── Module catalog (8 modules only) ─────────────────────
const MODULES = [
  { key: "store",        lbl: "Store",       icon: ShoppingBag,    plan: "Online Orders" },
  { key: "products",     lbl: "Products",    icon: Package,        plan: "Products" },
  { key: "sales",        lbl: "Sales",       icon: ShoppingCart,   plan: "Sales" },
  { key: "purchases",    lbl: "Purchases",   icon: FileText,       plan: "Purchases" },
  { key: "account",      lbl: "Account",     icon: Wallet,         plan: "Accounting" },
  { key: "bookings",     lbl: "Bookings",    icon: Calendar,       plan: "Bookings" },
  { key: "billing",      lbl: "Billing",     icon: CreditCard,     plan: null },
  { key: "reports",      lbl: "Reports",     icon: BarChart3,      plan: "Reports" },
  { key: "settings",     lbl: "Settings",    icon: Settings,       plan: null },
  { key: "reels",        lbl: "Reels",       icon: Film,           plan: null },  // public
  { key: "marketplace",  lbl: "Marketplace", icon: Store,          plan: "Marketplace" },
];

// Bottom-nav (Home · Reels [center accent] · Sales · Stock · More — per latest user spec)
const BOTTOM_NAV = ["home", "reels", "sales", "products", "more"];

// ───────────────────── Plan lock helper ─────────────────────
function useEntitlements() {
  const tenant = getTenant();
  const enabled = new Set(tenant?.modules_enabled || []);
  const hasEntitlements = (tenant?.modules_enabled || []).length > 0;
  return { isLocked: (plan) => !!plan && hasEntitlements && !enabled.has(plan) };
}

// ───────────────────── ROOT ─────────────────────
export default function MobileApp() {
  return (
    <CurrencyProvider>
      <Routes>
        <Route path="/" element={<SplashScreen />} />
        <Route path="/onboarding" element={<Onboarding />} />
        <Route path="/login" element={<MobileLogin />} />
        <Route path="/:subdomain" element={<MobileShell />}>
          <Route index element={<Navigate to="home" replace />} />
          <Route path="home"      element={<HomeRouter />} />
          <Route path="store"     element={<OrdersPage />} />
          <Route path="products"  element={<ModulePage k="products" />} />
          <Route path="products/new" element={<AddProductPage />} />
          <Route path="sales"     element={<ModulePage k="sales" />} />
          <Route path="sales/new" element={<NewSalePage />} />
          <Route path="purchases" element={<ModulePage k="purchases" />} />
          <Route path="account"   element={<ModulePage k="account" />} />
          <Route path="bookings"  element={<ModulePage k="bookings" />} />
          <Route path="billing"   element={<ModulePage k="billing" />} />
          <Route path="reports"   element={<ModulePage k="reports" />} />
          <Route path="settings"  element={<SettingsPage />} />
          <Route path="reels"     element={<MobileReelsFeed />} />
          <Route path="reels/upload" element={<ReelUploadPage />} />
          <Route path="reels/manage" element={<MyReelsPage />} />
          <Route path="reels/live" element={<GoLivePage />} />
          <Route path="marketplace" element={<MarketplaceBrowse />} />
          <Route path="marketplace/listings" element={<TenantListingsPage />} />
          <Route path="more"      element={<MorePage />} />
        </Route>
      </Routes>
    </CurrencyProvider>
  );
}

// ───────────────────── SPLASH ─────────────────────
function SplashScreen() {
  const nav = useNavigate();
  useEffect(() => {
    const seen = localStorage.getItem("brivo_mob_seen_onboarding");
    const t = setTimeout(() => {
      const tenant = getTenant();
      if (getToken() && tenant?.subdomain) nav(`/m/${tenant.subdomain}/home`, { replace: true });
      else if (seen) nav("/m/guest/home", { replace: true }); // guest discover is the default landing
      else nav("/m/onboarding", { replace: true });
    }, 1700);
    return () => clearTimeout(t);
  }, [nav]);
  return (
    <div data-testid="m-splash" className="min-h-screen bg-gradient-to-br from-[#1B4332] via-[#16834D] to-[#1FA868] text-white flex flex-col items-center justify-center px-8">
      <div className="relative">
        <div className="w-28 h-28 rounded-3xl bg-white/15 backdrop-blur-sm flex items-center justify-center shadow-2xl animate-[pulse_2s_ease-in-out_infinite]">
          <svg width="56" height="56" viewBox="0 0 24 24" fill="none">
            <path d="M4 6h12a4 4 0 010 8H4V6z" stroke="white" strokeWidth="2.5" strokeLinejoin="round" />
            <path d="M4 14h14a4 4 0 010 8H4v-8z" stroke="white" strokeWidth="2.5" strokeLinejoin="round" />
          </svg>
        </div>
        <div className="absolute -inset-4 rounded-full border-2 border-white/20 animate-ping" />
      </div>
      <div className="mt-9 text-center">
        <div className="font-display font-extrabold text-4xl tracking-tight">Brivo</div>
        <div className="mt-2 text-sm font-medium text-white/70 tracking-[0.3em] uppercase">Business OS</div>
      </div>
      <div className="mt-12 flex items-center gap-2">
        <span className="w-2 h-2 rounded-full bg-white/60 animate-bounce" style={{ animationDelay: "0ms" }} />
        <span className="w-2 h-2 rounded-full bg-white/60 animate-bounce" style={{ animationDelay: "150ms" }} />
        <span className="w-2 h-2 rounded-full bg-white/60 animate-bounce" style={{ animationDelay: "300ms" }} />
      </div>
    </div>
  );
}

// ───────────────────── ONBOARDING (4 screens) ─────────────────────
const SLIDES = [
  { icon: Activity,   title: "Live business overview",
    text: "Track sales, profit, and performance with a real-time dashboard tailored for mobile.",
    accent: "from-[#52B788] to-[#1B4332]" },
  { icon: Package,    title: "Manage products & inventory",
    text: "Add products, monitor stock and update prices straight from your pocket.",
    accent: "from-[#74C69D] to-[#16834D]" },
  { icon: TrendingUp, title: "Track sales & purchases",
    text: "Sell faster, log purchases, and watch your numbers grow — synced everywhere.",
    accent: "from-[#1FA868] to-[#0F5132]" },
  { icon: BarChart3,  title: "Reports & growth insights",
    text: "Beautiful charts, PnL summaries and stock reports built for decision-makers on the go.",
    accent: "from-[#2D6A4F] to-[#081C15]" },
];

function Onboarding() {
  const nav = useNavigate();
  const [i, setI] = useState(0);
  const slide = SLIDES[i];
  const Icon = slide.icon;
  const finish = () => {
    localStorage.setItem("brivo_mob_seen_onboarding", "1");
    nav("/m/guest/home", { replace: true });
  };
  const next = () => i === SLIDES.length - 1 ? finish() : setI(i + 1);
  return (
    <div data-testid="m-onboarding" className="min-h-screen bg-[#FFFCF2] flex flex-col">
      <div className="flex items-center justify-between px-6 pt-6">
        <div className="font-display font-extrabold text-lg text-black">Brivo</div>
        <button onClick={finish} data-testid="m-onb-skip" className="text-sm font-semibold text-black/55 hover:text-black px-3 py-1.5 rounded-lg">Skip</button>
      </div>
      <div className="flex-1 flex flex-col items-center justify-center px-8 text-center">
        <div className={`w-44 h-44 rounded-[2rem] bg-gradient-to-br ${slide.accent} flex items-center justify-center text-white shadow-2xl mb-10 transition-all duration-500`}>
          <Icon size={64} strokeWidth={1.6} />
        </div>
        <h2 data-testid="m-onb-title" className="font-display font-extrabold text-3xl text-black tracking-tight">{slide.title}</h2>
        <p className="mt-3 text-sm text-black/60 max-w-sm leading-relaxed">{slide.text}</p>
      </div>
      <div className="px-8 pb-9">
        <div className="flex justify-center gap-1.5 mb-7">
          {SLIDES.map((_, idx) => (
            <span key={idx} className={`h-1.5 rounded-full transition-all ${idx === i ? "w-7 bg-[#1FA868]" : "w-1.5 bg-black/15"}`} />
          ))}
        </div>
        <div className="flex items-center justify-between gap-3">
          <button onClick={() => setI(Math.max(0, i - 1))} disabled={i === 0} data-testid="m-onb-back"
            className="px-4 py-3 text-sm font-semibold text-black/60 disabled:opacity-30">
            <ArrowLeft size={16} />
          </button>
          <button onClick={next} data-testid="m-onb-next"
            className="flex-1 py-3.5 rounded-2xl bg-gradient-to-r from-[#1FA868] to-[#16834D] text-white font-bold inline-flex items-center justify-center gap-2 shadow-lg shadow-[#1FA868]/30">
            {i === SLIDES.length - 1 ? "Get Started" : "Next"} <ArrowRight size={16} />
          </button>
        </div>
      </div>
    </div>
  );
}

// ───────────────────── LOGIN ─────────────────────
function MobileLogin() {
  const nav = useNavigate();
  const [form, setForm] = useState({ subdomain: "", email: "", password: "" });
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e?.preventDefault();
    if (!form.subdomain || !form.email || !form.password) return toast.error("All fields required");
    setBusy(true);
    try {
      // Validate workspace first
      await axios.get(`${API}/tenants/lookup/${form.subdomain.toLowerCase()}`);
      const { data } = await axios.post(`${API}/auth/login`, {
        subdomain: form.subdomain.toLowerCase(),
        email: form.email,
        password: form.password,
      });
      setSession({ token: data.token, user: data.user, tenant: data.tenant });
      toast.success(`Welcome back, ${data.user.name?.split(" ")[0] || "there"}!`);
      nav(`/m/${data.tenant.subdomain}/home`, { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || (err.response?.status === 404 ? "Workspace not found" : "Login failed"));
    } finally { setBusy(false); }
  };
  return (
    <div data-testid="m-login" className="min-h-screen bg-[#FFFCF2] flex flex-col">
      <div className="px-6 pt-7 pb-4">
        <button onClick={() => nav("/m/onboarding")} className="text-sm text-black/60 inline-flex items-center gap-1.5"><ArrowLeft size={14} /> Back</button>
      </div>
      <div className="flex-1 px-6 pb-9 flex flex-col">
        <div>
        <img 
            src="https://brivo360.com/images/brivo-logo2.png" 
            alt="Brivo360" 
            className="h-10 w-auto cursor-pointer"
          /><br /><br />
        </div>
        <h1 className="font-display font-extrabold text-3xl text-black tracking-tight">Welcome back</h1>
        <p className="mt-1.5 text-sm text-black/55">Sign in to your Brivo workspace.</p>
        <form onSubmit={submit} className="mt-7 space-y-4">
          <Field lbl="Workspace">
            <div className="flex items-stretch rounded-2xl border border-black/12 bg-white">
              <input data-testid="m-login-subdomain" value={form.subdomain} onChange={(e) => setForm({ ...form, subdomain: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "") })}
                className="flex-1 px-4 py-3.5 rounded-l-2xl outline-none text-sm" placeholder="acme" autoCapitalize="none" />
              <span className="px-3 flex items-center text-xs text-black/45 border-l border-black/8 font-mono bg-[#FAFBFB] rounded-r-2xl">.brivopos.com</span>
            </div>
          </Field>
          <Field lbl="Email">
            <input type="email" autoCapitalize="none" data-testid="m-login-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="w-full px-4 py-3.5 rounded-2xl border border-black/12 bg-white outline-none text-sm focus:border-[#1FA868]" placeholder="you@business.com" />
          </Field>
          <Field lbl="Password">
            <div className="relative">
              <input type={show ? "text" : "password"} data-testid="m-login-password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
                className="w-full px-4 py-3.5 pr-11 rounded-2xl border border-black/12 bg-white outline-none text-sm focus:border-[#1FA868]" placeholder="••••••••" />
              <button type="button" onClick={() => setShow(!show)} className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 text-black/40">
                {show ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </Field>
          <button type="submit" disabled={busy} data-testid="m-login-submit"
            className="w-full py-3.5 mt-2 rounded-2xl bg-gradient-to-r from-[#1FA868] to-[#16834D] text-white font-bold inline-flex items-center justify-center gap-2 shadow-lg shadow-[#1FA868]/30 disabled:opacity-60">
            {busy ? "Signing in…" : "Sign in"} <ArrowRight size={16} />
          </button>
        </form>
        <Link to="/m/guest/home" data-testid="m-login-guest"
          className="mt-4 w-full py-3.5 rounded-2xl border-2 border-black/10 text-black font-bold inline-flex items-center justify-center gap-2 active:bg-black/5">
          Continue as guest <ArrowRight size={15} />
        </Link>
        <div className="mt-4 text-center text-xs text-black/55">
          Browse Reels & Marketplace without signing in.
        </div>
        {/* <div className="mt-auto pt-7 text-center text-xs text-black/55">
          Don't have a workspace?{" "}
          <Link to="/register" className="text-[#1FA868] font-bold">Create one</Link>
        </div> */}
      </div>
    </div>
  );
}

function Field({ lbl, children }) {
  return (
    <label className="block">
      <span className="block text-xs font-bold uppercase tracking-wider text-black/55 mb-1.5">{lbl}</span>
      {children}
    </label>
  );
}

// ───────────────────── SHELL ─────────────────────
function MobileShell() {
  const { subdomain } = useParams();
  const tenant = getTenant();
  const token = getToken();
  const isGuest = subdomain === "guest";
  if (!isGuest && (!token || !tenant || tenant.subdomain !== subdomain)) {
    return <Navigate to="/m/login" replace />;
  }
  return (
    <div data-testid={isGuest ? "m-shell-guest" : "m-shell"} className="min-h-screen bg-[#F6F7F9] pb-20">
      <MobileTopbar isGuest={isGuest} />
      <main className="min-h-[calc(100vh-72px)]">
        <Outlet />
      </main>
      {isGuest ? <GuestBottomNav /> : <BottomNav />}
    </div>
  );
}

function GuestBottomNav() {
  const loc = useLocation();
  const items = [
    { key: "home",        lbl: "Discover",    icon: Home },
    { key: "reels",       lbl: "Reels",       icon: Film },
    { key: "marketplace", lbl: "Marketplace", icon: Store },
    { key: "login",       lbl: "Sign in",     icon: LogOut, external: "/m/login" },
  ];
  return (
    <nav data-testid="m-bottom-nav-guest" className="fixed bottom-0 left-0 right-0 z-30 bg-white/95 backdrop-blur border-t border-black/8 px-1 pb-[env(safe-area-inset-bottom)]">
      <div className="flex items-stretch h-16 max-w-md mx-auto">
        {items.map(({ key, lbl, icon: Icon, external }) => {
          const path = external || `/m/guest/${key}`;
          const active = !external && (loc.pathname === path || loc.pathname.startsWith(path + "/"));
          return (
            <Link key={key} to={path} data-testid={`m-gnav-${key}`}
              className={`flex-1 flex flex-col items-center justify-center gap-0.5 py-1 transition-colors ${active ? "text-[#1FA868]" : "text-black/55"}`}>
              <Icon size={20} strokeWidth={active ? 2.4 : 1.8} />
              <span className={`text-[10px] font-${active ? "bold" : "semibold"} tracking-wide`}>{lbl}</span>
              {active && <span className="absolute top-0 w-8 h-0.5 rounded-b-full bg-[#1FA868]" />}
            </Link>
          );
        })}
      </div>
    </nav>
  );
}

function MobileTopbar({ isGuest }) {
  const tenant = getTenant();
  const loc = useLocation();
  const nav = useNavigate();
  const { subdomain } = useParams();
  const homePath = isGuest ? "/m/guest/home" : `/m/${subdomain}/home`;
  // Show back arrow on every page EXCEPT bottom-nav root pages
  const rootList = isGuest
    ? ["/home", "/reels", "/marketplace"]
    : ["/home", "/reels", "/sales", "/products", "/more"];
  const isRoot = rootList.some((p) => loc.pathname === `/m/${subdomain}${p}`);
  const onReels = loc.pathname.endsWith("/reels");
  // Topbar is translucent over reels so video fills the entire screen
  const containerCls = onReels
    ? "fixed top-0 left-0 right-0 z-40 bg-gradient-to-b from-black/65 via-black/35 to-transparent px-3 h-14 flex items-center gap-2 text-white"
    : "sticky top-0 z-30 bg-white border-b border-black/8 px-3 h-14 flex items-center gap-2";
  const iconCls = onReels ? "text-white" : "text-black/70";
  const labelMutedCls = onReels ? "text-white/70" : "text-black/45";
  const labelCls = onReels ? "text-white" : "text-black";
  return (
    <header className={containerCls}>
      {!isRoot ? (
        <button onClick={() => (window.history.length > 1 ? nav(-1) : nav(homePath))}
          data-testid="m-topbar-back"
          className={`p-2 rounded-xl ${onReels ? "hover:bg-white/10" : "hover:bg-black/5"}`}>
          <ArrowLeft size={18} className={iconCls} />
        </button>
      ) : (
        <div>
         <img 
            src="https://brivo360.com/images/fav.png" 
            alt="Brivo360" 
            className="h-10 w-auto cursor-pointer"
          />
        </div>
      )}
      <div className="flex-1 min-w-0 ml-1">
        {isGuest ? (
          <>
            <div className={`text-[10px] uppercase tracking-wider font-bold leading-none ${labelMutedCls}`}>Hi Guest!</div>
            <div className={`font-bold text-sm truncate ${labelCls}`}>Brivo360</div>
          </>
        ) : (
          <>
            <div className={`text-[10px] uppercase tracking-wider font-bold leading-none ${labelMutedCls}`}>Workspace</div>
            <div className={`font-bold text-sm truncate ${labelCls}`}>{tenant?.company_name}</div>
          </>
        )}
      </div>
      {isGuest ? (
        <Link to="/m/login" data-testid="m-guest-signin"
          className="px-3 py-1.5 rounded-lg bg-[#1FA868] text-white text-xs font-bold">Sign in</Link>
      ) : (
        <>
          <button data-testid="m-bell" className={`relative p-2 rounded-xl ${onReels ? "hover:bg-white/10" : "hover:bg-black/5"}`}>
            <Bell size={17} className={onReels ? "text-white/85" : "text-black/55"} />
            <span className="absolute top-1.5 right-1.5 w-1.5 h-1.5 rounded-full bg-rose-500" />
          </button>
          <button data-testid="m-logout" onClick={() => { clearSession(); nav("/m/login", { replace: true }); }}
            className={`p-2 rounded-xl ${onReels ? "hover:bg-white/10 text-white/85" : "hover:bg-rose-50 hover:text-rose-600 text-black/55"}`}>
            <LogOut size={17} />
          </button>
        </>
      )}
    </header>
  );
}

function BottomNav() {
  const { subdomain } = useParams();
  const loc = useLocation();
  const items = [
    { key: "home",        lbl: "Home",           icon: Home },
    { key: "reels",       lbl: "Reels",          icon: Film,           accent: true },
    { key: "sales",       lbl: "Sales",          icon: ShoppingCart },
    { key: "marketplace", lbl: "Marketplace",    icon: Store },
    { key: "more",        lbl: "More",           icon: Menu },
  ];
  // Reels feed is fully transparent — keep bottom nav visible but with translucent background
  const onReels = loc.pathname.endsWith("/reels");
  return (
    <nav data-testid="m-bottom-nav"
      className={`fixed bottom-0 left-0 right-0 z-40 px-1 pb-[env(safe-area-inset-bottom)] ${onReels ? "bg-black/55 backdrop-blur-md border-t border-white/10" : "bg-white/95 backdrop-blur border-t border-black/8"}`}>
      <div className="flex items-stretch h-16 max-w-md mx-auto">
        {items.map(({ key, lbl, icon: Icon, accent }) => {
          const path = `/m/${subdomain}/${key}`;
          const active = loc.pathname === path || loc.pathname.startsWith(path + "/");
          const baseColor = onReels ? "text-white/75" : "text-black/55";
          if (accent) {
            return (
              <Link key={key} to={path} data-testid={`m-nav-${key}`}
                className="flex-1 flex flex-col items-center justify-center gap-0.5 relative">
                <div className={`w-12 h-12 -mt-5 rounded-2xl flex items-center justify-center shadow-lg ring-4 ${onReels ? "ring-black/40" : "ring-white"} ${active ? "bg-gradient-to-br from-[#1FA868] to-[#16834D]" : "bg-black"}`}>
                  <Icon size={22} strokeWidth={2.4} className="text-white" />
                </div>
                <span className={`text-[10px] font-bold tracking-wide ${active ? "text-[#1FA868]" : onReels ? "text-white/90" : "text-black/70"}`}>{lbl}</span>
              </Link>
            );
          }
          return (
            <Link key={key} to={path} data-testid={`m-nav-${key}`}
              className={`flex-1 flex flex-col items-center justify-center gap-0.5 py-1 transition-colors ${active ? "text-[#1FA868]" : baseColor}`}>
              <Icon size={20} strokeWidth={active ? 2.4 : 1.8} />
              <span className={`text-[10px] font-${active ? "bold" : "semibold"} tracking-wide`}>{lbl}</span>
              {active && <span className="absolute top-0 w-8 h-0.5 rounded-b-full bg-[#1FA868]" />}
            </Link>
          );
        })}
      </div>
    </nav>
  );
}

// ───────────────────── HOME ─────────────────────
// HomeRouter switches between tenant Home and Guest Discover page based on subdomain
function HomeRouter() {
  const { subdomain } = useParams();
  if (subdomain === "guest") return <GuestDiscoverPage />;
  return <HomePage />;
}

// Guest landing — Brivo-branded discover hero with quick entry to Reels and Marketplace
function GuestDiscoverPage() {
  const [trending, setTrending] = useState([]);
  useEffect(() => {
    axios.get(`${API}/public/marketplace?sort=trending`).then((r) => setTrending((r.data?.trending || []).slice(0, 6))).catch(() => {});
  }, []);
  return (
    <div data-testid="m-guest-home" className="px-4 py-5 pb-24 space-y-5">
      <div className="rounded-3xl bg-gradient-to-br from-[#1B4332] via-[#16834D] to-[#1FA868] text-white p-6 relative overflow-hidden">
        <div className="absolute -top-16 -right-16 w-48 h-48 rounded-full bg-white/10 blur-3xl" />
        <div className="text-[10px] uppercase tracking-wider font-bold opacity-80">Welcome</div>
        <h1 className="mt-1 font-display font-extrabold text-3xl tracking-tight">Discover African brands.</h1>
        <p className="mt-1.5 text-sm text-white/85">Watch Reels, shop the Marketplace, follow stores.</p>
        <div className="mt-5 flex gap-2">
          <Link to="/m/guest/reels" data-testid="m-guest-cta-reels"
            className="flex-1 px-4 py-2.5 rounded-xl bg-white text-[#1B4332] font-bold text-sm inline-flex items-center justify-center gap-1.5">
            <Film size={14} /> Watch Reels
          </Link>
          <Link to="/m/guest/marketplace" data-testid="m-guest-cta-marketplace"
            className="flex-1 px-4 py-2.5 rounded-xl bg-black/30 backdrop-blur border border-white/25 text-white font-bold text-sm inline-flex items-center justify-center gap-1.5">
            <Store size={14} /> Marketplace
          </Link>
        </div>
      </div>

      <Section title="🔥 Trending">
        <div className="grid grid-cols-2 gap-3">
          {trending.map((l) => (
            <Link key={l.id} to={`/store/${l.tenant_subdomain}`} target="_blank"
              className="bg-white rounded-2xl border border-black/8 overflow-hidden active:scale-[0.98] transition-transform">
              <div className="aspect-square bg-[#FAFBFB] flex items-center justify-center">
                {l.image_url ? <img src={`${process.env.REACT_APP_BACKEND_URL}${l.image_url}`} alt="" className="w-full h-full object-cover" /> : <Package size={28} className="text-black/15" />}
              </div>
              <div className="p-2.5">
                <div className="font-bold text-xs text-black truncate">{l.title}</div>
                <div className="text-[10px] text-black/55 truncate">{l.tenant_name}</div>
                <div className="mt-1 font-extrabold text-sm text-[#1FA868]">₦{Number(l.price || 0).toLocaleString()}</div>
              </div>
            </Link>
          ))}
          {trending.length === 0 && (
            <div className="col-span-2 bg-white rounded-2xl border border-dashed border-black/12 p-8 text-center">
              <Sparkles size={24} className="mx-auto text-black/25 mb-2" />
              <div className="font-bold text-sm text-black">Marketplace is just getting started</div>
              <div className="text-xs text-black/55 mt-1">Check back soon for trending products.</div>
            </div>
          )}
        </div>
      </Section>

      <div className="bg-white rounded-2xl border border-black/8 p-5 text-center">
        <div className="font-bold text-black">Run a business?</div>
        <div className="text-xs text-black/55 mt-1">Brivo gives you a storefront, POS, inventory, and reels — all in one place.</div>
        <Link to="/m/login" data-testid="m-guest-register"
          className="mt-4 inline-flex px-5 py-2.5 rounded-xl bg-[#1FA868] text-white font-bold text-sm">Start free →</Link>
      </div>
    </div>
  );
}

// Login-required helper — call requireLogin() before protected actions.
// Returns true if logged in (action can proceed); false if not (toast shown).
function useRequireLogin() {
  const nav = useNavigate();
  return () => {
    if (getToken()) return true;
    toast("Sign in to continue", {
      description: "Like, comment, follow and purchases require an account.",
      action: { label: "Sign in", onClick: () => nav("/m/login") },
    });
    return false;
  };
}

function HomePage() {
  const { subdomain } = useParams();
  const user = getUser();
  const { fmt: fmtMoney } = useCurrency();
  const [period, setPeriod] = useState("D");
  const [overview, setOverview] = useState(null);
  const [periodData, setPeriodData] = useState(null);

  useEffect(() => {
    axios.get(`${API}/t/overview`, { headers: { Authorization: `Bearer ${getToken()}` } })
      .then((r) => setOverview(r.data)).catch(() => {});
  }, []);

  // Compute period-aggregated sales/profit from /t/sales (single fetch, client filter)
  useEffect(() => {
    setPeriodData(null);
    axios.get(`${API}/t/sales`, { headers: { Authorization: `Bearer ${getToken()}` } })
      .then((r) => {
        const sales = Array.isArray(r.data) ? r.data : (r.data?.items || []);
        const now = new Date();
        const startOf = (kind) => {
          const d = new Date(now);
          if (kind === "D") d.setHours(0, 0, 0, 0);
          else if (kind === "W") { d.setDate(d.getDate() - 7); d.setHours(0, 0, 0, 0); }
          else if (kind === "M") d.setDate(1);
          else if (kind === "Y") { d.setMonth(0, 1); d.setHours(0, 0, 0, 0); }
          return d;
        };
        const start = startOf(period);
        const within = sales.filter((s) => {
          const dt = new Date(s.date || s.created_at || 0);
          return dt >= start;
        });
        const total = within.reduce((a, s) => a + Number(s.total || 0), 0);
        const cogs = within.reduce((a, s) => a + (s.items || []).reduce((b, i) => b + Number((i.cost_price || 0) * (i.qty || 0)), 0), 0);
        setPeriodData({ revenue: total, profit: Math.max(0, total - cogs), orders: within.length });
      }).catch(() => setPeriodData({ revenue: 0, profit: 0, orders: 0 }));
  }, [period]);

  const greet = (() => { const h = new Date().getHours(); return h < 12 ? "Good morning" : h < 18 ? "Good afternoon" : "Good evening"; })();
  const periodLbl = { D: "Today", W: "Last 7 days", M: "This month", Y: "This year" }[period];

  return (
    <div data-testid="m-home" className="px-4 py-5 space-y-5">
      <div>
        <div className="text-xs font-bold uppercase tracking-wider text-[#1FA868] flex items-center gap-1.5">
          <span className="w-1.5 h-1.5 rounded-full bg-[#1FA868] animate-pulse" /> Live overview
        </div>
        <h1 className="mt-1 font-display font-extrabold text-3xl text-black tracking-tight">{greet}, {user?.name?.split(" ")[0] || "there"} 👋</h1>
        <p className="text-sm text-black/55 mt-1">Here's your business at a glance.</p>
      </div>

      {/* Hero — Sales prominent, profit small */}
      <div className="rounded-3xl bg-gradient-to-br from-[#1B4332] via-[#16834D] to-[#1FA868] text-white p-5 shadow-xl shadow-[#1FA868]/20 relative overflow-hidden">
        <div className="absolute -top-16 -right-16 w-48 h-48 rounded-full bg-white/10 blur-2xl" />
        <div className="relative">
          <div className="flex items-center justify-between">
            <div className="text-xs font-bold uppercase tracking-[0.2em] opacity-80 flex items-center gap-1.5">
              <ShoppingCart size={12} /> Sales · {periodLbl}
            </div>
            <div className="flex items-center gap-0.5 bg-white/15 rounded-full p-0.5" data-testid="m-period-toggle">
              {["D", "W", "M", "Y"].map((k) => (
                <button key={k} onClick={() => setPeriod(k)} data-testid={`m-period-${k}`}
                  className={`text-[10px] font-bold px-2.5 py-1 rounded-full transition-all ${period === k ? "bg-white text-[#1B4332]" : "text-white/85"}`}>
                  {k}
                </button>
              ))}
            </div>
          </div>
          <div className="mt-2 font-display font-extrabold text-4xl tracking-tight" data-testid="m-sales-amount">
            {periodData ? fmtMoney(periodData.revenue) : "—"}
          </div>
          {/* Profit very small under sales */}
          <div className="mt-1 text-[11px] font-semibold opacity-80 flex items-center gap-1.5" data-testid="m-profit-amount">
            <TrendingUp size={10} /> Profit {periodData ? fmtMoney(periodData.profit) : "—"}
            <span className="opacity-65">· {periodData?.orders ?? 0} orders</span>
          </div>
        </div>
      </div>

      {/* Quick actions */}
      <Section title="Quick actions">
        <div className="grid grid-cols-2 gap-3">
          <QuickCard to={`/m/${subdomain}/sales/new`} icon={ShoppingCart} bg="bg-emerald-50" tint="text-emerald-600" lbl="New Sale" testid="m-qa-new-sale" />
          <QuickCard to={`/m/${subdomain}/products/new`} icon={Package} bg="bg-sky-50" tint="text-sky-600" lbl="Add Product" testid="m-qa-add-product" />
          <QuickCard to={`/m/${subdomain}/store`} icon={ShoppingBag} bg="bg-amber-50" tint="text-amber-600" lbl="View Orders" testid="m-qa-orders" />
          <QuickCard to={`/m/${subdomain}/reports`} icon={BarChart3} bg="bg-violet-50" tint="text-violet-600" lbl="Reports" testid="m-qa-reports" />
        </div>
      </Section>

      <Section title="All modules">
        <div className="grid grid-cols-3 gap-3">
          {MODULES.filter((m) => m.key !== "reels" && m.key !== "marketplace").map((m) => <ModuleTile key={m.key} m={m} />)}
        </div>
      </Section>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <div className="text-[10px] font-bold uppercase opacity-70 tracking-wider">{label}</div>
      <div className="mt-1 font-bold text-sm">{value}</div>
    </div>
  );
}

function Section({ title, children, action }) {
  return (
    <section>
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-display font-bold text-lg text-black">{title}</h2>
        {action}
      </div>
      {children}
    </section>
  );
}

function QuickCard({ to, icon: Icon, lbl, bg, tint, testid }) {
  return (
    <Link to={to} data-testid={testid} className="bg-white rounded-2xl border border-black/8 p-4 active:scale-[0.97] transition-transform">
      <div className={`w-10 h-10 rounded-xl ${bg} ${tint} flex items-center justify-center`}>
        <Icon size={18} strokeWidth={2.2} />
      </div>
      <div className="mt-2.5 font-bold text-sm text-black">{lbl}</div>
    </Link>
  );
}

function ModuleTile({ m }) {
  const { subdomain } = useParams();
  const { isLocked } = useEntitlements();
  const locked = isLocked(m.plan);
  const Icon = m.icon;
  const cls = "relative rounded-2xl border p-3.5 flex flex-col items-center justify-center text-center min-h-[92px] active:scale-[0.97] transition-transform";
  if (locked) {
    return (
      <Link to={`/m/${subdomain}/billing`} data-testid={`m-tile-${m.key}`} className={`${cls} bg-amber-50/40 border-amber-200/60`}>
        <div className="absolute top-1.5 right-1.5"><Lock size={11} className="text-amber-600" /></div>
        <Icon size={22} className="text-amber-600 opacity-80" strokeWidth={1.8} />
        <div className="mt-2 text-[11px] font-bold text-black/65">{m.lbl}</div>
        <div className="mt-0.5 text-[8px] font-bold uppercase tracking-wider text-amber-700">Upgrade</div>
      </Link>
    );
  }
  return (
    <Link to={`/m/${subdomain}/${m.key}`} data-testid={`m-tile-${m.key}`} className={`${cls} bg-white border-black/8`}>
      <Icon size={22} className="text-[#1FA868]" strokeWidth={1.8} />
      <div className="mt-2 text-[11px] font-bold text-black">{m.lbl}</div>
    </Link>
  );
}

// ───────────────────── MORE PAGE ─────────────────────
function MorePage() {
  const { subdomain } = useParams();
  const tenant = getTenant();
  const user = getUser();
  const { isLocked } = useEntitlements();
  const nav = useNavigate();
  return (
    <div data-testid="m-more" className="px-4 py-5 space-y-5">
      <div className="bg-white rounded-3xl border border-black/8 p-5 flex items-center gap-4">
        <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#1FA868] to-[#16834D] text-white flex items-center justify-center font-extrabold text-xl">
          {(user?.name || "U")[0]}
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-bold text-base text-black truncate">{user?.name || "User"}</div>
          <div className="text-xs text-black/55 truncate">{tenant?.company_name} · {tenant?.plan_name}</div>
        </div>
      </div>
      <Section title="Modules">
        <div className="bg-white rounded-2xl border border-black/8 divide-y divide-black/6 overflow-hidden">
          {MODULES.map((m) => {
            const Icon = m.icon;
            const locked = isLocked(m.plan);
            return (
              <Link key={m.key} to={locked ? `/m/${subdomain}/billing` : `/m/${subdomain}/${m.key}`}
                data-testid={`m-row-${m.key}`}
                className="flex items-center gap-3 px-4 py-3.5 active:bg-black/3">
                <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${locked ? "bg-amber-50 text-amber-600" : "bg-emerald-50 text-[#1FA868]"}`}>
                  <Icon size={16} strokeWidth={2} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm text-black">{m.lbl}</div>
                  {locked && <div className="text-[10px] font-bold uppercase tracking-wider text-amber-700 mt-0.5">Upgrade required</div>}
                </div>
                {locked ? <Lock size={14} className="text-amber-600" /> : <ChevronRight size={16} className="text-black/35" />}
              </Link>
            );
          })}
        </div>
      </Section>
      <button onClick={() => { clearSession(); nav("/m/login", { replace: true }); }} data-testid="m-logout-row"
        className="w-full bg-white rounded-2xl border border-black/8 p-4 text-rose-600 font-bold text-sm flex items-center justify-center gap-2">
        <LogOut size={15} /> Log out
      </button>
    </div>
  );
}

// ───────────────────── GENERIC MODULE PAGE ─────────────────────
const FETCHERS = {
  store:     async () => (await axios.get(`${API}/t/online-orders`, { headers: { Authorization: `Bearer ${getToken()}` } })).data,
  products:  async () => (await axios.get(`${API}/t/products`, { headers: { Authorization: `Bearer ${getToken()}` } })).data,
  sales:     async () => (await axios.get(`${API}/t/sales`, { headers: { Authorization: `Bearer ${getToken()}` } })).data,
  purchases: async () => (await axios.get(`${API}/t/purchases`, { headers: { Authorization: `Bearer ${getToken()}` } })).data,
  account:   async () => (await axios.get(`${API}/t/accounting/expenses`, { headers: { Authorization: `Bearer ${getToken()}` } })).data,
  bookings:  async () => (await axios.get(`${API}/t/bookings`, { headers: { Authorization: `Bearer ${getToken()}` } })).data,
  reports:   async () => (await axios.get(`${API}/t/overview`, { headers: { Authorization: `Bearer ${getToken()}` } })).data,
  billing:   async () => (await axios.get(`${API}/t/billing/overview`, { headers: { Authorization: `Bearer ${getToken()}` } })).data,
};

function ModulePage({ k }) {
  const m = MODULES.find((x) => x.key === k);
  const { isLocked } = useEntitlements();
  const locked = isLocked(m?.plan);
  if (locked) return <LockedView m={m} />;

  // Billing & Reports get custom rich layouts
  if (k === "billing") return <BillingView />;
  if (k === "reports") return <ReportsView />;
  return <ListView mk={k} title={m.lbl} />;
}

function LockedView({ m }) {
  const { subdomain } = useParams();
  const Icon = m?.icon || Lock;
  return (
    <div data-testid="m-locked" className="px-5 py-10 flex flex-col items-center justify-center text-center min-h-[60vh]">
      <div className="w-20 h-20 rounded-3xl bg-amber-50 border border-amber-200 flex items-center justify-center mb-6">
        <Icon size={36} className="text-amber-600" strokeWidth={1.6} />
      </div>
      <h2 className="font-display font-extrabold text-2xl text-black tracking-tight">{m?.lbl} is locked</h2>
      <p className="mt-2 text-sm text-black/55 max-w-sm">This module isn't included in your current plan. Upgrade to unlock it for your team.</p>
      <Link to={`/m/${subdomain}/billing`} data-testid="m-locked-upgrade"
        className="mt-7 px-7 py-3.5 rounded-2xl bg-gradient-to-r from-[#1FA868] to-[#16834D] text-white font-bold text-sm inline-flex items-center gap-2 shadow-lg shadow-[#1FA868]/30">
        Upgrade plan <ArrowRight size={15} />
      </Link>
    </div>
  );
}

function ListView({ mk, title }) {
  const { subdomain } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [q, setQ] = useState("");
  const { fmt: fmtMoney } = useCurrency();
  useEffect(() => {
    setData(null); setErr(null);
    FETCHERS[mk]().then((d) => setData(Array.isArray(d) ? d : (d?.items || d?.data || []))).catch((e) => setErr(e.response?.data?.detail || "Could not load"));
  }, [mk]);

  const fabPath = mk === "products" ? `/m/${subdomain}/products/new`
                : mk === "sales"    ? `/m/${subdomain}/sales/new`
                : null;

  const matches = (row) => {
    if (!q) return true;
    const needle = q.toLowerCase();
    const hay = [row.name, row.invoice_number, row.order_number, row.reference, row.customer_name, row.supplier_name, row.sku, row.code]
      .filter(Boolean).join(" ").toLowerCase();
    return hay.includes(needle);
  };
  const filtered = (data || []).filter(matches);

  return (
    <div data-testid={`m-${mk}-page`} className="px-4 py-5 pb-24">
      <div className="flex items-center justify-between mb-4">
        <h1 className="font-display font-extrabold text-2xl text-black tracking-tight">{title}</h1>
      </div>
      {/* Search bar */}
      <div className="relative mb-4">
        <Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-black/35" />
        <input type="search" value={q} onChange={(e) => setQ(e.target.value)}
          data-testid={`m-${mk}-search`}
          placeholder={`Search ${title.toLowerCase()}…`}
          className="w-full pl-10 pr-3.5 py-3 rounded-2xl border border-black/10 bg-white text-sm focus:border-[#1FA868] outline-none placeholder:text-black/35" />
      </div>
      {err && <ErrorBanner msg={err} />}
      {data === null && !err && <ListSkeleton />}
      {data && filtered.length === 0 && (q ? (
        <div className="bg-white rounded-2xl border border-dashed border-black/10 p-8 text-center text-sm text-black/55">
          No results for "<span className="font-bold text-black">{q}</span>"
        </div>
      ) : <EmptyState mk={mk} />)}
      {data && filtered.length > 0 && (
        <div className="space-y-2.5" data-testid={`m-${mk}-list`}>
          {filtered.slice(0, 100).map((row) => <RowCard key={row.id || row._id || JSON.stringify(row).slice(0, 30)} row={row} mk={mk} fmtMoney={fmtMoney} />)}
        </div>
      )}
      {/* Floating action button (FAB) */}
      {fabPath && (
        <Link to={fabPath} data-testid={`m-${mk}-fab`}
          className="fixed right-5 bottom-24 w-14 h-14 rounded-2xl bg-gradient-to-br from-[#1FA868] to-[#16834D] text-white flex items-center justify-center shadow-xl shadow-[#1FA868]/30 active:scale-95 transition-transform z-20">
          <Plus size={22} strokeWidth={2.4} />
        </Link>
      )}
    </div>
  );
}

function RowCard({ row, mk, fmtMoney }) {
  const cfg = {
    store:     { primary: row.order_number || row.id, secondary: `${row.customer_name || "Guest"} · ${row.payment_method || ""}`, right: fmtMoney(row.total || 0), tag: row.status, icon: ShoppingBag },
    products:  { primary: row.name, secondary: row.sku ? `SKU ${row.sku}` : (row.category_name || ""), right: fmtMoney(row.price_sale || row.selling_price || 0), tag: row.in_stock < 5 ? "Low" : null, icon: Package },
    sales:     { primary: row.invoice_number || row.id, secondary: row.customer_name || "Walk-in", right: fmtMoney(row.total || 0), tag: row.payment_status, icon: ShoppingCart },
    purchases: { primary: row.reference || row.invoice_number, secondary: row.supplier_name || "—", right: fmtMoney(row.total || 0), tag: row.status, icon: FileText },
    account:   { primary: row.reference || row.note, secondary: row.category_name || row.account_name || "—", right: fmtMoney(row.amount || 0), tag: row.date, icon: Wallet },
    bookings:  { primary: row.title || row.reference || row.id, secondary: `${row.customer_name || "—"} · ${row.start_at || row.date || ""}`, right: row.status || "Booked", tag: null, icon: Calendar },
  }[mk] || { primary: row.id, secondary: "", right: "", tag: null, icon: Activity };
  const Icon = cfg.icon;
  return (
    <div className="bg-white rounded-2xl border border-black/8 p-3.5 flex items-center gap-3 active:scale-[0.99] transition-transform">
      <div className="w-10 h-10 rounded-xl bg-emerald-50 text-[#1FA868] flex items-center justify-center flex-shrink-0">
        <Icon size={16} strokeWidth={2} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-bold text-sm text-black truncate">{cfg.primary}</div>
        <div className="text-[11px] text-black/55 truncate mt-0.5">{cfg.secondary}</div>
      </div>
      <div className="text-right flex-shrink-0">
        <div className="font-bold text-sm text-black">{cfg.right}</div>
        {cfg.tag && (
          <span className="mt-1 inline-block text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-md bg-emerald-50 text-emerald-700">
            {String(cfg.tag).slice(0, 14)}
          </span>
        )}
      </div>
    </div>
  );
}

function ListSkeleton() {
  return (
    <div className="space-y-2.5">
      {[0, 1, 2, 3, 4].map((i) => (
        <div key={i} className="bg-white rounded-2xl border border-black/8 p-3.5 flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-black/5 animate-pulse" />
          <div className="flex-1 space-y-2">
            <div className="h-3 w-2/5 rounded bg-black/5 animate-pulse" />
            <div className="h-2.5 w-3/5 rounded bg-black/5 animate-pulse" />
          </div>
          <div className="h-4 w-12 rounded bg-black/5 animate-pulse" />
        </div>
      ))}
    </div>
  );
}

function EmptyState({ mk }) {
  return (
    <div className="bg-white rounded-3xl border border-dashed border-black/12 p-9 text-center">
      <div className="w-14 h-14 rounded-2xl bg-emerald-50 text-[#1FA868] mx-auto flex items-center justify-center"><Plus size={20} /></div>
      <div className="mt-3 font-bold text-black">Nothing here yet</div>
      <div className="text-xs text-black/55 mt-1">Add your first {mk} from the desktop dashboard.</div>
    </div>
  );
}

function ErrorBanner({ msg }) {
  return (
    <div className="bg-rose-50 border border-rose-200 text-rose-700 rounded-2xl p-3.5 flex items-start gap-2.5 mb-3">
      <AlertCircle size={16} className="flex-shrink-0 mt-0.5" />
      <div className="text-xs font-medium">{msg}</div>
    </div>
  );
}

// ───────────────────── BILLING ─────────────────────
function BillingView() {
  const tenant = getTenant();
  const { fmt: fmtMoney } = useCurrency();
  const [data, setData] = useState(null);
  useEffect(() => {
    axios.get(`${API}/t/billing/overview`, { headers: { Authorization: `Bearer ${getToken()}` } })
      .then((r) => setData(r.data)).catch(() => {});
  }, []);
  const inv = data?.invoices?.slice(0, 5) || [];
  return (
    <div data-testid="m-billing-page" className="px-4 py-5 space-y-5">
      <h1 className="font-display font-extrabold text-2xl text-black tracking-tight">Billing</h1>
      <div className="rounded-3xl bg-gradient-to-br from-[#1FA868] to-[#16834D] text-white p-5 shadow-xl shadow-[#1FA868]/20">
        <div className="text-xs font-bold uppercase opacity-80 tracking-[0.2em]">Current plan</div>
        <div className="mt-2 font-display font-extrabold text-3xl tracking-tight">{tenant?.plan_name || "Starter"}</div>
        <div className="mt-1 text-sm opacity-85">{fmtMoney(tenant?.amount || 0)} · {tenant?.billing_cycle || "monthly"}</div>
        <div className="mt-4 inline-flex items-center px-2.5 py-1 rounded-full bg-white/20 text-[10px] font-bold uppercase tracking-wider">
          {tenant?.status === "active" ? "Active" : (tenant?.status || "—")}
        </div>
      </div>
      {data?.usage && (
        <Section title="Usage">
          <div className="bg-white rounded-2xl border border-black/8 divide-y divide-black/6">
            {[
              ["Products", data.usage.products, data.plan?.limits?.products],
              ["Users", data.usage.users, data.plan?.limits?.users],
              ["Customers", data.usage.customers, data.plan?.limits?.customers],
            ].map(([lbl, used, lim]) => {
              const pct = lim === -1 ? 0 : Math.min(100, Math.round(((used || 0) / (lim || 1)) * 100));
              return (
                <div key={lbl} className="px-4 py-3">
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-xs font-semibold text-black/65">{lbl}</span>
                    <span className="text-xs font-bold text-black">{used || 0}{lim !== -1 && ` / ${lim || 0}`}</span>
                  </div>
                  {lim !== -1 && (
                    <div className="h-1.5 rounded-full bg-black/5">
                      <div className={`h-full rounded-full ${pct > 85 ? "bg-rose-500" : "bg-[#1FA868]"}`} style={{ width: `${pct}%` }} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </Section>
      )}
      <Section title="Recent invoices">
        {inv.length === 0 ? (
          <div className="bg-white rounded-2xl border border-black/8 p-6 text-center text-sm text-black/55">No invoices yet</div>
        ) : (
          <div className="space-y-2.5">
            {inv.map((i) => (
              <div key={i.id || i.reference} className="bg-white rounded-2xl border border-black/8 p-3.5 flex items-center justify-between">
                <div>
                  <div className="font-bold text-sm text-black">{i.reference || i.id}</div>
                  <div className="text-[11px] text-black/55 mt-0.5">{i.created_at?.slice(0, 10) || ""}</div>
                </div>
                <div className="text-right">
                  <div className="font-bold text-sm text-black">{fmtMoney(i.amount || 0)}</div>
                  <div className="text-[10px] font-bold uppercase tracking-wider text-emerald-700">{i.status || "Paid"}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </Section>
      <a href={`/t/${tenant?.subdomain}/billing`} data-testid="m-billing-upgrade-link"
        className="block w-full text-center py-3.5 rounded-2xl bg-black text-white font-bold text-sm">
        Manage plan on web
      </a>
    </div>
  );
}

// ───────────────────── REPORTS ─────────────────────
function ReportsView() {
  const { fmt: fmtMoney } = useCurrency();
  const [data, setData] = useState(null);
  useEffect(() => {
    axios.get(`${API}/t/overview`, { headers: { Authorization: `Bearer ${getToken()}` } })
      .then((r) => setData(r.data)).catch(() => {});
  }, []);
  const s = data?.stats || {};
  return (
    <div data-testid="m-reports-page" className="px-4 py-5 space-y-5">
      <h1 className="font-display font-extrabold text-2xl text-black tracking-tight">Reports</h1>
      <div className="grid grid-cols-2 gap-3">
        <ReportTile lbl="Revenue" v={fmtMoney(s.revenue || 0)} bg="from-emerald-500 to-emerald-700" />
        <ReportTile lbl="Expenses" v={fmtMoney(s.expenses || 0)} bg="from-sky-500 to-sky-700" />
        <ReportTile lbl="Profit" v={fmtMoney(s.profit || 0)} bg="from-amber-500 to-amber-700" />
        <ReportTile lbl="Orders" v={s.invoice_count || 0} bg="from-violet-500 to-violet-700" />
      </div>
      {data?.top_products?.length > 0 && (
        <Section title="Top products">
          <div className="bg-white rounded-2xl border border-black/8 divide-y divide-black/6">
            {data.top_products.slice(0, 5).map((p, i) => (
              <div key={p.id || i} className="flex items-center gap-3 px-4 py-3">
                <div className="w-8 h-8 rounded-lg bg-emerald-50 text-[#1FA868] flex items-center justify-center text-xs font-bold">#{i + 1}</div>
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-sm text-black truncate">{p.name || p.product_name}</div>
                  <div className="text-[11px] text-black/55">{p.sales_count || p.qty || 0} sold</div>
                </div>
                <div className="font-bold text-sm text-black">{fmtMoney(p.revenue || p.total || 0)}</div>
              </div>
            ))}
          </div>
        </Section>
      )}
    </div>
  );
}

function ReportTile({ lbl, v, bg }) {
  return (
    <div className={`rounded-2xl bg-gradient-to-br ${bg} text-white p-4 shadow-md`}>
      <div className="text-[10px] font-bold uppercase tracking-[0.2em] opacity-85">{lbl}</div>
      <div className="mt-1.5 font-display font-extrabold text-xl tracking-tight">{v}</div>
    </div>
  );
}

// ───────────────────── ADD PRODUCT ─────────────────────
function AddProductPage() {
  const { subdomain } = useParams();
  const nav = useNavigate();
  const [form, setForm] = useState({
    name: "", code: "", price: "", cost_price: "", opening_stock: "", description: "",
    category_id: "", brand_id: "", unit_id: "",
  });
  const [busy, setBusy] = useState(false);
  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const submit = async (e) => {
    e?.preventDefault();
    if (!form.name.trim()) return toast.error("Product name is required");
    if (!form.price) return toast.error("Selling price is required");
    setBusy(true);
    try {
      const payload = {
        name: form.name.trim(),
        code: form.code || null,
        price: Number(form.price),
        cost_price: Number(form.cost_price || 0),
        opening_stock: Number(form.opening_stock || 0),
        description: form.description || "",
        category_id: form.category_id || null,
        brand_id: form.brand_id || null,
        unit_id: form.unit_id || null,
      };
      await axios.post(`${API}/t/products`, payload, { headers: { Authorization: `Bearer ${getToken()}` } });
      toast.success("Product added");
      nav(`/m/${subdomain}/products`, { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save");
    } finally { setBusy(false); }
  };
  return (
    <div data-testid="m-add-product" className="px-4 py-5 pb-24">
      <h1 className="font-display font-extrabold text-2xl text-black tracking-tight mb-1">Add Product</h1>
      <p className="text-sm text-black/55 mb-5">Create a new product in your catalog.</p>
      <form onSubmit={submit} className="space-y-4">
        <Field lbl="Product Name *">
          <input value={form.name} onChange={(e) => upd("name", e.target.value)} data-testid="m-prod-name"
            placeholder="e.g. Espresso Machine"
            className="w-full px-4 py-3.5 rounded-2xl border border-black/12 bg-white text-sm outline-none focus:border-[#1FA868]" />
        </Field>
        <Field lbl="Product Code (SKU)">
          <input value={form.code} onChange={(e) => upd("code", e.target.value)} data-testid="m-prod-code"
            placeholder="Auto-generated if blank"
            className="w-full px-4 py-3.5 rounded-2xl border border-black/12 bg-white text-sm outline-none focus:border-[#1FA868]" />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field lbl="Selling Price *">
            <input type="number" inputMode="decimal" value={form.price} onChange={(e) => upd("price", e.target.value)}
              data-testid="m-prod-price"
              className="w-full px-4 py-3.5 rounded-2xl border border-black/12 bg-white text-sm outline-none focus:border-[#1FA868]" />
          </Field>
          <Field lbl="Cost Price">
            <input type="number" inputMode="decimal" value={form.cost_price} onChange={(e) => upd("cost_price", e.target.value)}
              data-testid="m-prod-cost"
              className="w-full px-4 py-3.5 rounded-2xl border border-black/12 bg-white text-sm outline-none focus:border-[#1FA868]" />
          </Field>
        </div>
        <Field lbl="Opening Stock">
          <input type="number" inputMode="numeric" value={form.opening_stock} onChange={(e) => upd("opening_stock", e.target.value)}
            data-testid="m-prod-stock"
            className="w-full px-4 py-3.5 rounded-2xl border border-black/12 bg-white text-sm outline-none focus:border-[#1FA868]" />
        </Field>
        <Field lbl="Description">
          <textarea value={form.description} onChange={(e) => upd("description", e.target.value)} rows={3}
            data-testid="m-prod-desc"
            className="w-full px-4 py-3 rounded-2xl border border-black/12 bg-white text-sm outline-none focus:border-[#1FA868]" />
        </Field>
        <button type="submit" disabled={busy} data-testid="m-prod-save"
          className="w-full py-3.5 rounded-2xl bg-gradient-to-r from-[#1FA868] to-[#16834D] text-white font-bold inline-flex items-center justify-center gap-2 shadow-lg shadow-[#1FA868]/30 disabled:opacity-60">
          <Save size={15} /> {busy ? "Saving…" : "Save Product"}
        </button>
      </form>
    </div>
  );
}

// ───────────────────── NEW SALE ─────────────────────
function NewSalePage() {
  const { subdomain } = useParams();
  const nav = useNavigate();
  const { fmt: fmtMoney } = useCurrency();
  const [products, setProducts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [warehouseId, setWarehouseId] = useState("");
  const [customerId, setCustomerId] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("cash");
  const [paid, setPaid] = useState(0);
  const [search, setSearch] = useState("");
  const [cart, setCart] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const auth = { headers: { Authorization: `Bearer ${getToken()}` } };
    axios.get(`${API}/t/products`, auth).then((r) => setProducts(r.data || [])).catch(() => {});
    axios.get(`${API}/t/customers`, auth).then((r) => setCustomers(r.data || [])).catch(() => {});
    axios.get(`${API}/t/warehouses`, auth).then((r) => {
      setWarehouses(r.data || []);
      if (r.data?.[0]) setWarehouseId(r.data[0].id);
    }).catch(() => {});
  }, []);

  const filteredProducts = useMemo(() => {
    if (!search) return products.slice(0, 30);
    const n = search.toLowerCase();
    return products.filter((p) => p.name?.toLowerCase().includes(n) || p.sku?.toLowerCase().includes(n) || p.code?.toLowerCase().includes(n)).slice(0, 50);
  }, [products, search]);

  const addItem = (p) => {
    setCart((c) => {
      const existing = c.find((x) => x.product_id === p.id);
      if (existing) return c.map((x) => x.product_id === p.id ? { ...x, qty: x.qty + 1 } : x);
      return [...c, { product_id: p.id, name: p.name, qty: 1, unit_price: Number(p.price || 0) }];
    });
  };
  const updateQty = (pid, delta) => {
    setCart((c) => c.flatMap((x) => {
      if (x.product_id !== pid) return [x];
      const q = x.qty + delta;
      return q <= 0 ? [] : [{ ...x, qty: q }];
    }));
  };
  const removeItem = (pid) => setCart((c) => c.filter((x) => x.product_id !== pid));
  const subtotal = cart.reduce((a, x) => a + x.qty * x.unit_price, 0);

  const submit = async () => {
    if (cart.length === 0) return toast.error("Add at least one product");
    setBusy(true);
    try {
      await axios.post(`${API}/t/sales`, {
        customer_id: customerId || null,
        warehouse_id: warehouseId || null,
        items: cart,
        payment_method: paymentMethod,
        paid: Number(paid || 0) || subtotal,
        status: "completed",
      }, { headers: { Authorization: `Bearer ${getToken()}` } });
      toast.success("Sale recorded");
      nav(`/m/${subdomain}/sales`, { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save sale");
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="m-new-sale" className="px-4 py-5 pb-40">
      <h1 className="font-display font-extrabold text-2xl text-black tracking-tight mb-1">New Sale</h1>
      <p className="text-sm text-black/55 mb-4">Add items, pick customer & payment, then complete.</p>

      <div className="space-y-3">
        <Field lbl="Customer">
          <select value={customerId} onChange={(e) => setCustomerId(e.target.value)} data-testid="m-sale-customer"
            className="w-full px-4 py-3.5 rounded-2xl border border-black/12 bg-white text-sm outline-none focus:border-[#1FA868]">
            <option value="">Walk-in Customer</option>
            {customers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </Field>
        {warehouses.length > 1 && (
          <Field lbl="Warehouse">
            <select value={warehouseId} onChange={(e) => setWarehouseId(e.target.value)} data-testid="m-sale-warehouse"
              className="w-full px-4 py-3.5 rounded-2xl border border-black/12 bg-white text-sm outline-none focus:border-[#1FA868]">
              {warehouses.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
            </select>
          </Field>
        )}
      </div>

      <div className="mt-5">
        <div className="text-xs font-bold uppercase tracking-wider text-black/55 mb-2">Add Products</div>
        <div className="relative">
          <Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-black/35" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} data-testid="m-sale-product-search"
            placeholder="Search by name or code…"
            className="w-full pl-10 pr-3.5 py-3 rounded-2xl border border-black/10 bg-white text-sm focus:border-[#1FA868] outline-none" />
        </div>
        <div className="mt-2 max-h-56 overflow-y-auto bg-white rounded-2xl border border-black/8 divide-y divide-black/6">
          {filteredProducts.length === 0 ? (
            <div className="p-5 text-center text-sm text-black/55">No products found</div>
          ) : filteredProducts.map((p) => (
            <button key={p.id} onClick={() => addItem(p)} data-testid={`m-sale-add-${p.id}`}
              className="w-full flex items-center gap-3 px-4 py-3 active:bg-black/5 text-left">
              <div className="w-10 h-10 rounded-xl bg-emerald-50 text-[#1FA868] flex items-center justify-center text-[10px] font-extrabold">
                {(p.name || "P").slice(0, 2).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-bold text-sm text-black truncate">{p.name}</div>
                <div className="text-[11px] text-black/55">{fmtMoney(p.price || 0)} · stock {p.stock || 0}</div>
              </div>
              <Plus size={16} className="text-[#1FA868]" />
            </button>
          ))}
        </div>
      </div>

      {cart.length > 0 && (
        <div className="mt-5 bg-white rounded-2xl border border-black/8 p-4">
          <div className="text-xs font-bold uppercase tracking-wider text-black/55 mb-2">Cart ({cart.length})</div>
          <div className="space-y-2.5">
            {cart.map((it) => (
              <div key={it.product_id} className="flex items-center gap-2">
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-sm text-black truncate">{it.name}</div>
                  <div className="text-[11px] text-black/55">{fmtMoney(it.unit_price)}</div>
                </div>
                <div className="flex items-center gap-1.5">
                  <button onClick={() => updateQty(it.product_id, -1)} data-testid={`m-cart-minus-${it.product_id}`}
                    className="p-1.5 rounded-lg bg-black/5 active:bg-black/10"><MinusCircle size={14} /></button>
                  <span className="w-7 text-center font-bold text-sm">{it.qty}</span>
                  <button onClick={() => updateQty(it.product_id, +1)} data-testid={`m-cart-plus-${it.product_id}`}
                    className="p-1.5 rounded-lg bg-black/5 active:bg-black/10"><PlusCircle size={14} /></button>
                  <button onClick={() => removeItem(it.product_id)} className="p-1.5 rounded-lg text-rose-500 active:bg-rose-50">
                    <Trash2 size={13} />
                  </button>
                </div>
                <div className="font-bold text-sm text-black w-20 text-right">{fmtMoney(it.qty * it.unit_price)}</div>
              </div>
            ))}
          </div>
          <div className="mt-3 pt-3 border-t border-black/8 flex items-center justify-between">
            <span className="text-xs font-bold uppercase tracking-wider text-black/55">Total</span>
            <span data-testid="m-sale-total" className="font-display font-extrabold text-2xl text-black">{fmtMoney(subtotal)}</span>
          </div>
        </div>
      )}

      <div className="mt-5 grid grid-cols-2 gap-3">
        <Field lbl="Payment Method">
          <select value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)} data-testid="m-sale-payment"
            className="w-full px-3 py-3.5 rounded-2xl border border-black/12 bg-white text-sm outline-none focus:border-[#1FA868]">
            <option value="cash">Cash</option>
            <option value="card">Card</option>
            <option value="transfer">Bank Transfer</option>
            <option value="paystack">Paystack</option>
          </select>
        </Field>
        <Field lbl="Amount Paid">
          <input type="number" inputMode="decimal" value={paid} onChange={(e) => setPaid(e.target.value)}
            placeholder={String(subtotal)} data-testid="m-sale-paid"
            className="w-full px-4 py-3.5 rounded-2xl border border-black/12 bg-white text-sm outline-none focus:border-[#1FA868]" />
        </Field>
      </div>

      <button onClick={submit} disabled={busy || cart.length === 0} data-testid="m-sale-submit"
        className="mt-5 w-full py-3.5 rounded-2xl bg-gradient-to-r from-[#1FA868] to-[#16834D] text-white font-bold inline-flex items-center justify-center gap-2 shadow-lg shadow-[#1FA868]/30 disabled:opacity-60">
        <Check size={15} /> {busy ? "Saving…" : "Complete Sale"}
      </button>
    </div>
  );
}

// ───────────────────── ORDERS (View Orders / approve) ─────────────────────
function OrdersPage() {
  const { isLocked } = useEntitlements();
  const m = MODULES.find((x) => x.key === "store");
  const locked = isLocked(m.plan);
  const [orders, setOrders] = useState(null);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const { fmt: fmtMoney } = useCurrency();

  useEffect(() => {
    if (locked) return;
    setOrders(null);
    axios.get(`${API}/t/store/orders`, { headers: { Authorization: `Bearer ${getToken()}` } })
      .then((r) => setOrders(r.data || [])).catch(() => setOrders([]));
  }, [locked]);

  if (locked) return <LockedView m={m} />;

  const reload = () => {
    setOrders(null);
    axios.get(`${API}/t/store/orders`, { headers: { Authorization: `Bearer ${getToken()}` } })
      .then((r) => setOrders(r.data || [])).catch(() => setOrders([]));
  };

  const approve = async (o, status) => {
    try {
      await axios.patch(`${API}/t/store/orders/${o.id}/status`, { status },
        { headers: { Authorization: `Bearer ${getToken()}` } });
      toast.success(status === "confirmed" ? "Order approved" : `Marked ${status}`);
      reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Action failed");
    }
  };

  const filtered = (orders || [])
    .filter((o) => filter === "all" ? true : o.status === filter)
    .filter((o) => !q || (o.order_number + " " + (o.customer_name || "") + " " + (o.customer_email || "")).toLowerCase().includes(q.toLowerCase()));

  return (
    <div data-testid="m-orders-page" className="px-4 py-5 pb-24">
      <h1 className="font-display font-extrabold text-2xl text-black tracking-tight mb-4">Orders</h1>
      <div className="relative mb-3">
        <Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-black/35" />
        <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="m-orders-search"
          placeholder="Search by order #, name, email…"
          className="w-full pl-10 pr-3.5 py-3 rounded-2xl border border-black/10 bg-white text-sm focus:border-[#1FA868] outline-none" />
      </div>
      <div className="flex gap-1.5 overflow-x-auto mb-4 pb-1 -mx-4 px-4">
        {[
          ["all", "All"],
          ["pending", "Pending"],
          ["confirmed", "Confirmed"],
          ["shipped", "Shipped"],
          ["delivered", "Delivered"],
          ["cancelled", "Cancelled"],
        ].map(([k, lbl]) => (
          <button key={k} onClick={() => setFilter(k)} data-testid={`m-orders-filter-${k}`}
            className={`px-3.5 py-1.5 rounded-full text-xs font-bold whitespace-nowrap transition-colors ${filter === k ? "bg-[#1B4332] text-white" : "bg-white text-black/65 border border-black/10"}`}>
            {lbl}
          </button>
        ))}
      </div>
      {orders === null && <ListSkeleton />}
      {orders && filtered.length === 0 && (
        <div className="bg-white rounded-2xl border border-dashed border-black/10 p-9 text-center">
          <ShoppingBag size={28} className="mx-auto text-black/25 mb-2" />
          <div className="text-sm font-bold text-black">No orders {filter !== "all" ? `(${filter})` : "yet"}</div>
          <div className="text-xs text-black/55 mt-1">Orders from your storefront will appear here.</div>
        </div>
      )}
      {orders && filtered.length > 0 && (
        <div className="space-y-2.5" data-testid="m-orders-list">
          {filtered.map((o) => <OrderCard key={o.id} o={o} approve={approve} fmtMoney={fmtMoney} />)}
        </div>
      )}
    </div>
  );
}

function OrderCard({ o, approve, fmtMoney }) {
  const tone = {
    pending: "bg-amber-50 text-amber-700 border-amber-200",
    confirmed: "bg-emerald-50 text-emerald-700 border-emerald-200",
    shipped: "bg-sky-50 text-sky-700 border-sky-200",
    delivered: "bg-emerald-50 text-emerald-700 border-emerald-200",
    cancelled: "bg-rose-50 text-rose-700 border-rose-200",
  }[o.status] || "bg-slate-50 text-slate-700 border-slate-200";

  return (
    <div className="bg-white rounded-2xl border border-black/8 p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="font-bold text-sm text-black">{o.order_number}</div>
          <div className="text-[11px] text-black/55 truncate mt-0.5">
            {o.customer_name || "Guest"} · {o.payment_method?.replace("_", " ") || "—"}
          </div>
          <div className="text-[11px] text-black/45 mt-0.5">{(o.created_at || "").slice(0, 10)}</div>
        </div>
        <div className="text-right">
          <div className="font-bold text-sm text-black">{fmtMoney(o.total || 0)}</div>
          <span className={`mt-1.5 inline-block text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-md border ${tone}`}>
            {o.status}
          </span>
        </div>
      </div>
      {(o.items || []).length > 0 && (
        <div className="mt-3 pt-3 border-t border-black/6 text-[11px] text-black/55">
          {o.items.length} item{o.items.length > 1 ? "s" : ""} · {o.items.slice(0, 2).map((i) => i.name).join(", ")}
          {o.items.length > 2 && ` +${o.items.length - 2} more`}
        </div>
      )}
      {o.status === "pending" && (
        <div className="mt-3 grid grid-cols-2 gap-2">
          <button onClick={() => approve(o, "confirmed")} data-testid={`m-order-approve-${o.id}`}
            className="py-2.5 rounded-xl bg-gradient-to-r from-[#1FA868] to-[#16834D] text-white text-xs font-bold inline-flex items-center justify-center gap-1.5 active:scale-95 transition-transform">
            <Check size={13} /> Approve
          </button>
          <button onClick={() => approve(o, "cancelled")} data-testid={`m-order-cancel-${o.id}`}
            className="py-2.5 rounded-xl bg-rose-50 text-rose-700 text-xs font-bold inline-flex items-center justify-center gap-1.5 active:scale-95 transition-transform">
            <X size={13} /> Cancel
          </button>
        </div>
      )}
      {o.status === "confirmed" && (
        <button onClick={() => approve(o, "shipped")} data-testid={`m-order-ship-${o.id}`}
          className="mt-3 w-full py-2.5 rounded-xl bg-sky-50 text-sky-700 text-xs font-bold inline-flex items-center justify-center gap-1.5">
          Mark as Shipped
        </button>
      )}
      {o.status === "shipped" && (
        <button onClick={() => approve(o, "delivered")} data-testid={`m-order-deliver-${o.id}`}
          className="mt-3 w-full py-2.5 rounded-xl bg-emerald-50 text-emerald-700 text-xs font-bold inline-flex items-center justify-center gap-1.5">
          Mark as Delivered
        </button>
      )}
    </div>
  );
}

// ───────────────────── SETTINGS ─────────────────────
function SettingsPage() {
  const [form, setForm] = useState(null);
  const [pw, setPw] = useState({ current_password: "", new_password: "" });
  const [logoBusy, setLogoBusy] = useState(false);

  const reload = () =>
    axios.get(`${API}/t/settings/company`, { headers: { Authorization: `Bearer ${getToken()}` } })
      .then((r) => setForm(r.data)).catch(() => {});
  useEffect(() => { reload(); }, []);

  if (!form) {
    return (
      <div data-testid="m-settings-loading" className="px-4 py-5">
        <h1 className="font-display font-extrabold text-2xl text-black tracking-tight mb-4">Settings</h1>
        <ListSkeleton />
      </div>
    );
  }

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const save = async () => {
    try {
      const payload = { ...form };
      delete payload.paystack_secret_key_masked;
      delete payload.paystack_secret_key_set;
      delete payload.admin_email; delete payload.subdomain; delete payload.plan_name;
      await axios.patch(`${API}/t/settings/company`, payload,
        { headers: { Authorization: `Bearer ${getToken()}` } });
      toast.success("Settings saved");
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const changePw = async () => {
    if (pw.new_password.length < 8) return toast.error("New password must be 8+ chars");
    try {
      await axios.post(`${API}/t/settings/change-password`, pw,
        { headers: { Authorization: `Bearer ${getToken()}` } });
      toast.success("Password updated");
      setPw({ current_password: "", new_password: "" });
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const onLogoFile = async (file) => {
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) return toast.error("Max 2MB");
    setLogoBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await fetch(`${API}/t/settings/upload-logo`, {
        method: "POST",
        headers: { Authorization: `Bearer ${getToken()}` },
        body: fd,
      });
      if (!r.ok) throw new Error((await r.json()).detail || "Upload failed");
      const data = await r.json();
      upd("logo_url", data.url);
      toast.success("Logo uploaded");
    } catch (e) { toast.error(e.message || "Upload failed"); }
    finally { setLogoBusy(false); }
  };

  return (
    <div data-testid="m-settings-page" className="px-4 py-5 pb-10 space-y-5">
      <h1 className="font-display font-extrabold text-2xl text-black tracking-tight">Settings</h1>

      {/* Logo */}
      <div className="bg-white rounded-2xl border border-black/8 p-4">
        <div className="text-xs font-bold uppercase tracking-wider text-black/55 mb-3 flex items-center gap-1.5">
          <ImageIcon size={12} /> Company Logo
        </div>
        <div className="flex items-center gap-4">
          <div className="w-20 h-20 rounded-2xl bg-[#FAFBFB] border border-dashed border-black/15 flex items-center justify-center overflow-hidden flex-shrink-0">
            {form.logo_url ? (
              <img src={`${process.env.REACT_APP_BACKEND_URL}${form.logo_url}`} alt="logo" className="max-w-full max-h-full object-contain" />
            ) : (
              <span className="text-[10px] text-black/40 text-center px-1">No logo</span>
            )}
          </div>
          <div className="flex-1 flex flex-col gap-2">
            <label className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-xl bg-[#1FA868] text-white text-xs font-bold active:opacity-90">
              <Upload size={13} /> {logoBusy ? "Uploading…" : (form.logo_url ? "Change" : "Upload")}
              <input type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" data-testid="m-settings-logo"
                onChange={(e) => onLogoFile(e.target.files?.[0])} className="hidden" />
            </label>
            {form.logo_url && (
              <button onClick={async () => { try { await axios.patch(`${API}/t/settings/company`, { logo_url: "" }, { headers: { Authorization: `Bearer ${getToken()}` } }); upd("logo_url", ""); toast.success("Logo removed"); } catch { toast.error("Failed"); } }}
                className="w-full px-3 py-2 rounded-xl border border-black/10 text-xs font-semibold text-black/60 active:bg-black/5">
                Remove
              </button>
            )}
            <div className="text-[10px] text-black/45">PNG / JPG / WebP / SVG · Max 2MB</div>
          </div>
        </div>
      </div>

      {/* Company info */}
      <div className="bg-white rounded-2xl border border-black/8 p-4 space-y-3">
        <div className="text-xs font-bold uppercase tracking-wider text-black/55 flex items-center gap-1.5">
          <Settings size={12} /> Company Information
        </div>
        <MField lbl="Company Name">
          <input value={form.company_name || ""} onChange={(e) => upd("company_name", e.target.value)}
            data-testid="m-settings-company-name"
            className="w-full px-3.5 py-3 rounded-xl border border-black/12 bg-white text-sm outline-none focus:border-[#1FA868]" />
        </MField>
        <MField lbl="Workspace URL" hint="Read-only">
          <div className="w-full px-3.5 py-3 rounded-xl border border-black/10 bg-[#FAFBFB] text-sm font-mono text-black/70">{form.subdomain}.brivopos.com</div>
        </MField>
        <div className="grid grid-cols-2 gap-3">
          <MField lbl="Phone">
            <input value={form.phone || ""} onChange={(e) => upd("phone", e.target.value)} data-testid="m-settings-phone"
              className="w-full px-3.5 py-3 rounded-xl border border-black/12 bg-white text-sm outline-none focus:border-[#1FA868]" />
          </MField>
          <MField lbl="Currency">
            <select value={form.currency || "NGN"} onChange={(e) => upd("currency", e.target.value)} data-testid="m-settings-currency"
              className="w-full px-3 py-3 rounded-xl border border-black/12 bg-white text-sm outline-none focus:border-[#1FA868]">
              {["NGN", "USD", "EUR", "GBP", "KES", "ZAR", "GHS"].map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </MField>
        </div>
        <MField lbl="Email">
          <input type="email" value={form.email || ""} onChange={(e) => upd("email", e.target.value)}
            data-testid="m-settings-email"
            className="w-full px-3.5 py-3 rounded-xl border border-black/12 bg-white text-sm outline-none focus:border-[#1FA868]" />
        </MField>
        <div className="grid grid-cols-2 gap-3">
          <MField lbl="City">
            <input value={form.city || ""} onChange={(e) => upd("city", e.target.value)}
              className="w-full px-3.5 py-3 rounded-xl border border-black/12 bg-white text-sm outline-none focus:border-[#1FA868]" />
          </MField>
          <MField lbl="Country">
            <input value={form.country || ""} onChange={(e) => upd("country", e.target.value)}
              className="w-full px-3.5 py-3 rounded-xl border border-black/12 bg-white text-sm outline-none focus:border-[#1FA868]" />
          </MField>
        </div>
        <MField lbl="Address">
          <textarea value={form.address || ""} onChange={(e) => upd("address", e.target.value)} rows={2}
            className="w-full px-3.5 py-2.5 rounded-xl border border-black/12 bg-white text-sm outline-none focus:border-[#1FA868]" />
        </MField>
        <button onClick={save} data-testid="m-settings-save"
          className="w-full py-3 rounded-xl bg-gradient-to-r from-[#1FA868] to-[#16834D] text-white font-bold text-sm inline-flex items-center justify-center gap-1.5">
          <Save size={14} /> Save Settings
        </button>
      </div>

      {/* Change password */}
      <div className="bg-white rounded-2xl border border-black/8 p-4 space-y-3">
        <div className="text-xs font-bold uppercase tracking-wider text-black/55 flex items-center gap-1.5">
          <Lock size={12} /> Change Password
        </div>
        <MField lbl="Current Password">
          <input type="password" value={pw.current_password} onChange={(e) => setPw({ ...pw, current_password: e.target.value })}
            data-testid="m-settings-cur-pw"
            className="w-full px-3.5 py-3 rounded-xl border border-black/12 bg-white text-sm outline-none focus:border-[#1FA868]" />
        </MField>
        <MField lbl="New Password" hint="Min 8 chars">
          <input type="password" value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })}
            data-testid="m-settings-new-pw"
            className="w-full px-3.5 py-3 rounded-xl border border-black/12 bg-white text-sm outline-none focus:border-[#1FA868]" />
        </MField>
        <button onClick={changePw} data-testid="m-settings-pw-save"
          className="w-full py-3 rounded-xl bg-black text-white font-bold text-sm inline-flex items-center justify-center gap-1.5">
          Update Password
        </button>
      </div>
    </div>
  );
}

function MField({ lbl, hint, children }) {
  return (
    <label className="block">
      <span className="block text-[11px] font-bold uppercase tracking-wider text-black/55 mb-1.5">
        {lbl}{hint && <span className="ml-1.5 font-normal normal-case tracking-normal text-black/40">· {hint}</span>}
      </span>
      {children}
    </label>
  );
}


// ───────────────────── REELS — Full-screen vertical feed ─────────────────────
function MobileReelsFeed() {
  const [reels, setReels] = useState(null);
  const [active, setActive] = useState(0);
  const { subdomain } = useParams();

  useEffect(() => {
    axios.get(`${API}/public/reels`).then((r) => setReels(r.data || [])).catch(() => setReels([]));
  }, []);

  // Track scroll to set the visible reel
  const onScroll = (e) => {
    const idx = Math.round(e.target.scrollTop / window.innerHeight);
    if (idx !== active) setActive(idx);
  };

  if (reels === null) {
    return <div data-testid="m-reels-loading" className="fixed inset-0 bg-black flex items-center justify-center text-white/65 text-sm">Loading reels…</div>;
  }
  if (reels.length === 0) {
    const isGuest = subdomain === "guest";
    return (
      <div data-testid="m-reels-empty" className="fixed inset-0 bg-black text-white flex flex-col items-center justify-center px-6 text-center">
        <Film size={48} className="opacity-40 mb-4" />
        <div className="font-bold text-lg">No reels yet</div>
        <div className="text-sm text-white/55 mt-1">
          {isGuest ? "Sign in with a Brivo workspace to upload your first reel." : "Be the first to upload — go to Reels Upload from Tenant Web."}
        </div>
        <Link to={isGuest ? "/m/login" : `/m/${subdomain}/reels/upload`} className="mt-6 px-5 py-2.5 rounded-full bg-white text-black text-sm font-bold">
          {isGuest ? "Sign in" : "Upload a reel"}
        </Link>
        <Link to={`/m/${subdomain}/home`} className="mt-3 text-xs text-white/55">← Back to home</Link>
      </div>
    );
  }

  const isGuest = subdomain === "guest";
  return (
    <div data-testid="m-reels-feed"
      className="fixed inset-0 bg-black overflow-y-scroll snap-y snap-mandatory touch-manipulation"
      onScroll={onScroll}
      style={{ scrollbarWidth: "none", touchAction: "pan-y" }}>
      <style>{`
        [data-testid=m-reels-feed]::-webkit-scrollbar{display:none}
        [data-testid=m-reels-feed] *{touch-action:pan-y;-webkit-touch-callout:none}
      `}</style>
      {reels.map((r, i) => (
        <ReelItem key={r.id} reel={r} active={i === active} subdomain={subdomain} />
      ))}
      {/* Floating action buttons (tenant-only) */}
      {!isGuest && (
        <div className="fixed top-20 right-4 z-50 flex flex-col gap-2">
          <Link to={`/m/${subdomain}/reels/upload`} data-testid="m-reels-upload-btn"
            className="px-3 py-1.5 rounded-full bg-white/20 backdrop-blur-md text-white text-xs font-bold inline-flex items-center gap-1 border border-white/20">
            <Upload size={12} /> Upload
          </Link>
          <Link to={`/m/${subdomain}/reels/live`} data-testid="m-reels-live-btn"
            className="px-3 py-1.5 rounded-full bg-gradient-to-r from-rose-500 to-rose-600 text-white text-xs font-bold inline-flex items-center gap-1 shadow-lg shadow-rose-500/40">
            <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" /> Go Live
          </Link>
        </div>
      )}
    </div>
  );
}

function ReelItem({ reel, active, subdomain }) {
  const [muted, setMuted] = useState(true);
  const [liked, setLiked] = useState(false);
  const [likeCount, setLikeCount] = useState(reel.like_count || 0);
  const [showComments, setShowComments] = useState(false);
  const [showReport, setShowReport] = useState(false);
  const [following, setFollowing] = useState(false);
  const [saved, setSaved] = useState(false);
  const [paused, setPaused] = useState(false);
  const [showPauseIcon, setShowPauseIcon] = useState(false);
  const videoRef = useRef(null);
  const requireLogin = useRequireLogin();
  const isGuest = subdomain === "guest";
  const isOwnReel = !isGuest && reel.tenant_subdomain === subdomain;

  // Auto play/pause based on visibility
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    if (active && !paused) v.play().catch(() => {});
    else v.pause();
  }, [active, paused]);

  useEffect(() => {
    if (active) axios.post(`${API}/public/reels/${reel.id}/view`).catch(() => {});
  }, [active, reel.id]);

  const togglePlay = () => {
    setPaused((p) => !p);
    setShowPauseIcon(true);
    setTimeout(() => setShowPauseIcon(false), 700);
  };

  const toggleLike = async () => {
    if (!requireLogin()) return;
    const newLiked = !liked;
    setLiked(newLiked);
    setLikeCount((c) => Math.max(0, c + (newLiked ? 1 : -1)));
    try { await axios.post(`${API}/public/reels/${reel.id}/like`, { liked: newLiked }); } catch {}
  };
  const toggleFollow = async () => {
    if (!requireLogin()) return;
    const newF = !following;
    setFollowing(newF);
    try { await axios.post(`${API}/public/stores/${reel.tenant_subdomain}/follow`, { action: newF ? "follow" : "unfollow" }); } catch {}
  };
  const shareReel = async () => {
    if (!requireLogin()) return;
    const url = `${window.location.origin}/m/${subdomain}/reels?id=${reel.id}`;
    try {
      if (navigator.share) await navigator.share({ title: reel.caption?.slice(0, 60), url });
      else { await navigator.clipboard.writeText(url); toast.success("Link copied"); }
    } catch {}
  };
  const toggleSave = () => {
    if (!requireLogin()) return;
    setSaved((s) => !s);
  };
  const openComments = () => {
    if (!requireLogin()) return;
    setShowComments(true);
  };

  return (
    <div data-testid={`m-reel-${reel.id}`} className="relative h-screen snap-start snap-always flex items-center justify-center bg-black overflow-hidden">
      <video ref={videoRef}
        src={`${process.env.REACT_APP_BACKEND_URL}${reel.video_url}`}
        poster={reel.thumbnail_url ? `${process.env.REACT_APP_BACKEND_URL}${reel.thumbnail_url}` : undefined}
        loop muted={muted} playsInline
        className="absolute inset-0 w-full h-full object-cover" />
      {/* Tap layer for pause/play */}
      <button onClick={togglePlay} data-testid={`m-reel-tap-${reel.id}`} aria-label="Toggle play"
        className="absolute inset-0 z-[1] bg-gradient-to-t from-black/60 via-transparent to-black/30" />
      {/* Pause/play overlay icon */}
      {showPauseIcon && (
        <div className="absolute inset-0 z-[5] flex items-center justify-center pointer-events-none">
          <div className="w-20 h-20 rounded-full bg-black/55 backdrop-blur flex items-center justify-center animate-[fadeOut_0.7s_ease-out]">
            {paused ? <Play size={36} className="text-white ml-1" fill="white" /> : <Pause size={36} className="text-white" fill="white" />}
          </div>
        </div>
      )}

      {/* Right floating actions */}
      <div className="absolute right-3 bottom-28 flex flex-col items-center gap-5 z-10">
        <button onClick={toggleFollow} data-testid={`m-reel-follow-${reel.id}`} className="relative">
          <div className="w-11 h-11 rounded-full bg-white/15 backdrop-blur border-2 border-white flex items-center justify-center text-white font-extrabold">
            {(reel.tenant_name || "S")[0]}
          </div>
          {!following && <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-5 h-5 rounded-full bg-[#1FA868] flex items-center justify-center"><Plus size={11} className="text-white" strokeWidth={3} /></div>}
          {following && <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-5 h-5 rounded-full bg-white flex items-center justify-center"><Check size={11} className="text-[#1FA868]" strokeWidth={3} /></div>}
        </button>
        <ActionBtn icon={Heart} count={likeCount} active={liked} onClick={toggleLike} testid={`m-reel-like-${reel.id}`} fill={liked ? "#ff3040" : ""} />
        <ActionBtn icon={MessageCircle} count={reel.comment_count} onClick={openComments} testid={`m-reel-comment-${reel.id}`} />
        <ActionBtn icon={Share2} onClick={shareReel} testid={`m-reel-share-${reel.id}`} />
        <ActionBtn icon={Bookmark} active={saved} onClick={toggleSave} testid={`m-reel-save-${reel.id}`} fill={saved ? "white" : ""} />
        <button onClick={() => setMuted((m) => !m)} data-testid={`m-reel-mute-${reel.id}`}
          className="w-10 h-10 rounded-full bg-white/15 backdrop-blur flex items-center justify-center text-white">
          {muted ? <VolumeX size={17} /> : <Volume2 size={17} />}
        </button>
        {/* Report reel — any viewer (guest or logged-in) can report a reel */}
        {!isOwnReel && (
          <button onClick={() => setShowReport(true)} data-testid={`m-reel-report-${reel.id}`}
            className="w-10 h-10 rounded-full bg-white/15 backdrop-blur flex items-center justify-center text-white">
            <Flag size={15} />
          </button>
        )}
        {/* My Reels — tenant only (manage own reels) */}
        {!isGuest && (
          <Link to={`/m/${subdomain}/reels/manage`} data-testid={`m-reel-my-reels-${reel.id}`}
            className="w-10 h-10 rounded-full bg-white/15 backdrop-blur flex items-center justify-center text-white">
            <Film size={17} />
          </Link>
        )}
      </div>

      {/* Bottom info */}
      <div className="absolute left-0 right-16 bottom-24 px-4 z-10 text-white pointer-events-none">
        <div className="flex items-center gap-2 mb-2 pointer-events-auto">
          <span className="font-extrabold text-base">{reel.tenant_name}</span>
          {!isOwnReel && (
            <button onClick={toggleFollow} data-testid={`m-reel-follow-pill-${reel.id}`}
              className={`text-[11px] px-2.5 py-0.5 rounded-full font-bold ${following ? "bg-white/15 border border-white/60 text-white" : "border border-white text-white"}`}>
              {following ? "Following" : "Follow"}
            </button>
          )}
        </div>
        <div className="text-sm leading-snug line-clamp-3">{reel.caption}</div>
        {(reel.hashtags || []).length > 0 && (
          <div className="mt-1.5 text-xs text-white/85">
            {reel.hashtags.map((h) => <span key={h} className="mr-1.5">#{h.replace(/^#/, "")}</span>)}
          </div>
        )}
        {(reel.products || []).length > 0 && (
          <div className="mt-3 flex gap-2 overflow-x-auto pb-1 pointer-events-auto">
            {reel.products.map((p) => (
              <Link key={p.id} to={`/store/${p.subdomain}`}
                className="flex items-center gap-2 px-2.5 py-1.5 rounded-full bg-white/15 backdrop-blur border border-white/25 text-xs font-bold whitespace-nowrap">
                <Tag size={11} /> {p.name?.slice(0, 18)} · ₦{Number(p.price || 0).toLocaleString()}
              </Link>
            ))}
          </div>
        )}
      </div>

      {showComments && <CommentsSheet reelId={reel.id} onClose={() => setShowComments(false)} />}
      {showReport && <ReportReelSheet reel={reel} onClose={() => setShowReport(false)} />}
    </div>
  );
}

function ActionBtn({ icon: Icon, count, active, onClick, testid, fill }) {
  return (
    <button onClick={onClick} data-testid={testid} className="flex flex-col items-center gap-0.5">
      <div className="w-11 h-11 rounded-full bg-white/15 backdrop-blur flex items-center justify-center text-white active:scale-90 transition-transform">
        <Icon size={20} fill={fill || "none"} stroke={active ? (fill || "white") : "white"} strokeWidth={active ? 2.5 : 2} />
      </div>
      {count !== undefined && <span className="text-white text-[10px] font-extrabold drop-shadow">{shortNum(count)}</span>}
    </button>
  );
}

function shortNum(n) {
  if (n >= 1e6) return (n / 1e6).toFixed(1) + "M";
  if (n >= 1e3) return (n / 1e3).toFixed(1) + "K";
  return String(n || 0);
}

function CommentsSheet({ reelId, onClose }) {
  const [comments, setComments] = useState([]);
  const [text, setText] = useState("");
  const requireLogin = useRequireLogin();

  useEffect(() => {
    axios.get(`${API}/public/reels/${reelId}/comments`).then((r) => setComments(r.data || [])).catch(() => {});
  }, [reelId]);

  const send = async () => {
    if (!requireLogin()) return;
    if (!text.trim()) return;
    try {
      const r = await axios.post(`${API}/public/reels/${reelId}/comments`, { text: text.trim() });
      setComments((c) => [r.data, ...c]);
      setText("");
    } catch (e) { toast.error("Could not send comment"); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/55 flex items-end pb-16" onClick={onClose}>
      <div className="bg-white w-full max-h-[70vh] rounded-t-3xl p-4 flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between pb-2 border-b border-black/8">
          <div className="font-bold text-base">{comments.length} comments</div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-black/5"><X size={16} /></button>
        </div>
        <div className="flex-1 overflow-y-auto py-3 space-y-3">
          {comments.length === 0 && <div className="text-center text-sm text-black/45 py-8">Be the first to comment</div>}
          {comments.map((c) => (
            <div key={c.id} className="flex gap-2.5">
              <div className="w-7 h-7 rounded-full bg-emerald-50 text-[#1FA868] flex items-center justify-center text-[10px] font-extrabold">{(c.author_name || "G")[0]}</div>
              <div className="flex-1">
                <div className="text-xs font-bold text-black">{c.author_name}</div>
                <div className="text-sm text-black/80">{c.text}</div>
                <div className="text-[10px] text-black/40 mt-0.5">{(c.created_at || "").slice(0, 10)}</div>
              </div>
            </div>
          ))}
        </div>
        <div className="flex gap-2 pt-2 border-t border-black/8">
          <input value={text} onChange={(e) => setText(e.target.value)} placeholder="Add a comment…"
            data-testid="m-reel-comment-input"
            onKeyDown={(e) => e.key === "Enter" && send()}
            className="flex-1 px-3.5 py-2.5 rounded-full border border-black/12 text-sm focus:border-[#1FA868] outline-none" />
          <button onClick={send} data-testid="m-reel-comment-send"
            className="w-10 h-10 rounded-full bg-[#1FA868] text-white flex items-center justify-center disabled:opacity-50" disabled={!text.trim()}>
            <Send size={15} />
          </button>
        </div>
      </div>
    </div>
  );
}

// ───────────────────── REPORT REEL SHEET ─────────────────────
const REPORT_REASONS = [
  "Spam or misleading",
  "Inappropriate or sexual content",
  "Violence or dangerous behaviour",
  "Hate speech or harassment",
  "Counterfeit or scam",
  "Copyright / intellectual property",
  "Other (describe below)",
];

function ReportReelSheet({ reel, onClose }) {
  const user = getUser();
  const [picked, setPicked] = useState("");
  const [detail, setDetail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async () => {
    const reason = picked === "Other (describe below)"
      ? detail.trim()
      : (picked + (detail.trim() ? " — " + detail.trim() : ""));
    if (!reason || reason.length < 5) {
      toast.error("Please pick a reason or add a short description (min 5 chars).");
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/public/reels/${reel.id}/report`, {
        reason,
        reporter_name: user?.name || "",
        reporter_email: user?.email || "",
      });
      setSent(true);
      setTimeout(onClose, 1400);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not submit report");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[55] bg-black/65 flex items-end pb-16" onClick={onClose} data-testid="m-report-sheet">
      <div className="bg-white w-full rounded-t-3xl p-5 flex flex-col max-h-[80vh]" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-3 pb-3 border-b border-black/8">
          <div className="w-9 h-9 rounded-xl bg-rose-50 text-rose-600 flex items-center justify-center"><Flag size={15} /></div>
          <div className="flex-1 min-w-0">
            <div className="font-display font-bold text-lg text-black leading-tight">Report this reel</div>
            <div className="text-xs text-black/55 truncate">by {reel.tenant_name}</div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-black/5"><X size={16} /></button>
        </div>

        {sent ? (
          <div className="py-12 text-center">
            <div className="w-14 h-14 mx-auto rounded-2xl bg-emerald-50 text-[#1FA868] flex items-center justify-center"><Check size={26} strokeWidth={2.4} /></div>
            <div className="mt-3 font-display font-bold text-lg text-black">Report submitted</div>
            <div className="mt-1 text-xs text-black/55">Our moderation team will review it shortly. Thank you for helping keep Brivo safe.</div>
          </div>
        ) : (
          <>
            <div className="overflow-y-auto pt-4 space-y-2">
              <div className="text-[11px] font-bold uppercase tracking-wider text-black/55 mb-1">Choose a reason</div>
              {REPORT_REASONS.map((r) => (
                <button key={r} onClick={() => setPicked(r)} data-testid={`m-report-reason-${r.split(" ")[0].toLowerCase()}`}
                  className={`w-full text-left px-3.5 py-3 rounded-xl border text-sm font-semibold transition-colors ${picked === r ? "border-[#1FA868] bg-emerald-50 text-[#1FA868]" : "border-black/10 bg-white text-black hover:border-black/30"}`}>
                  {r}
                </button>
              ))}
              <label className="block pt-2">
                <span className="block text-[11px] font-bold uppercase tracking-wider text-black/55 mb-1">
                  {picked === "Other (describe below)" ? "Describe the issue" : "Additional details (optional)"}
                </span>
                <textarea value={detail} onChange={(e) => setDetail(e.target.value)}
                  rows={3} maxLength={500} data-testid="m-report-detail"
                  placeholder="Tell us what's wrong with this reel…"
                  className="w-full px-3 py-2.5 rounded-xl border border-black/12 bg-white text-sm outline-none focus:border-[#1FA868]" />
              </label>
            </div>
            <button onClick={submit} disabled={busy || !picked} data-testid="m-report-submit"
              className="mt-4 w-full py-3 rounded-xl bg-rose-600 hover:bg-rose-700 text-white font-bold text-sm inline-flex items-center justify-center gap-1.5 disabled:opacity-50">
              <Flag size={13} /> {busy ? "Submitting…" : "Submit report"}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

// ───────────────────── REEL UPLOAD ─────────────────────
function ReelUploadPage() {
  const { subdomain } = useParams();
  const nav = useNavigate();
  const [form, setForm] = useState({ caption: "", hashtags: "", visibility: "public", music_title: "" });
  const [videoUrl, setVideoUrl] = useState("");
  const [thumbUrl, setThumbUrl] = useState("");
  const [products, setProducts] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios.get(`${API}/t/products`, { headers: { Authorization: `Bearer ${getToken()}` } })
      .then((r) => setProducts(r.data || [])).catch(() => {});
  }, []);

  const onVideo = async (file) => {
    if (!file) return;
    if (file.size > 80 * 1024 * 1024) return toast.error("Video too large (max 80MB)");
    const fd = new FormData(); fd.append("file", file);
    setBusy(true);
    try {
      const r = await fetch(`${API}/t/reels/upload-video`, { method: "POST", headers: { Authorization: `Bearer ${getToken()}` }, body: fd });
      if (!r.ok) throw new Error("Upload failed");
      const data = await r.json();
      setVideoUrl(data.url);
      toast.success("Video uploaded");
    } catch (e) { toast.error("Upload failed"); }
    finally { setBusy(false); }
  };
  const onThumb = async (file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try {
      const r = await fetch(`${API}/t/reels/upload-thumb`, { method: "POST", headers: { Authorization: `Bearer ${getToken()}` }, body: fd });
      const data = await r.json();
      setThumbUrl(data.url);
      toast.success("Thumbnail uploaded");
    } catch (e) { toast.error("Upload failed"); }
  };

  const submit = async () => {
    if (!videoUrl) return toast.error("Please upload a video first");
    setBusy(true);
    try {
      await axios.post(`${API}/t/reels`, {
        video_url: videoUrl, thumbnail_url: thumbUrl,
        caption: form.caption, music_title: form.music_title,
        hashtags: form.hashtags.split(/[,#\s]+/).filter(Boolean),
        product_ids: selectedProducts,
        visibility: form.visibility,
      }, { headers: { Authorization: `Bearer ${getToken()}` } });
      toast.success("Reel published!");
      nav(`/m/${subdomain}/reels`, { replace: true });
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to publish"); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="m-reel-upload" className="px-4 py-5 pb-10 space-y-4">
      <h1 className="font-display font-extrabold text-2xl text-black tracking-tight">New Reel</h1>

      <label className="block">
        <span className="block text-xs font-bold uppercase tracking-wider text-black/55 mb-1.5">Video *</span>
        {videoUrl ? (
          <div className="relative w-full aspect-[9/16] max-h-80 rounded-2xl overflow-hidden bg-black">
            <video src={`${process.env.REACT_APP_BACKEND_URL}${videoUrl}`} controls className="w-full h-full object-contain" />
            <button onClick={() => setVideoUrl("")} className="absolute top-2 right-2 w-8 h-8 rounded-full bg-black/60 text-white flex items-center justify-center"><X size={14} /></button>
          </div>
        ) : (
          <label className="block w-full p-8 rounded-2xl border-2 border-dashed border-black/15 hover:border-[#1FA868] transition-all text-center cursor-pointer">
            <Upload size={26} className="text-black/35 mx-auto mb-2" />
            <div className="text-sm font-bold text-black">Tap to upload video</div>
            <div className="text-xs text-black/45 mt-0.5">MP4, MOV, WebM · Max 80MB</div>
            <input type="file" accept="video/*" data-testid="m-reel-video-input" onChange={(e) => onVideo(e.target.files?.[0])} className="hidden" />
          </label>
        )}
      </label>

      <label className="block">
        <span className="block text-xs font-bold uppercase tracking-wider text-black/55 mb-1.5">Thumbnail (optional)</span>
        {thumbUrl ? (
          <div className="relative w-32 h-44 rounded-xl overflow-hidden bg-black/5">
            <img src={`${process.env.REACT_APP_BACKEND_URL}${thumbUrl}`} alt="" className="w-full h-full object-cover" />
            <button onClick={() => setThumbUrl("")} className="absolute top-1.5 right-1.5 w-6 h-6 rounded-full bg-black/60 text-white flex items-center justify-center"><X size={11} /></button>
          </div>
        ) : (
          <label className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-black/15 cursor-pointer">
            <ImageIcon size={14} /> <span className="text-sm font-semibold">Pick thumbnail</span>
            <input type="file" accept="image/*" onChange={(e) => onThumb(e.target.files?.[0])} className="hidden" />
          </label>
        )}
      </label>

      <Field lbl="Caption">
        <textarea value={form.caption} onChange={(e) => setForm({ ...form, caption: e.target.value })}
          data-testid="m-reel-caption" rows={3}
          placeholder="What's your reel about?"
          className="w-full px-4 py-3 rounded-2xl border border-black/12 bg-white text-sm outline-none focus:border-[#1FA868]" />
      </Field>
      <Field lbl="Hashtags (comma or space-separated)">
        <input value={form.hashtags} onChange={(e) => setForm({ ...form, hashtags: e.target.value })}
          data-testid="m-reel-hashtags"
          placeholder="fashion, summer, sale"
          className="w-full px-4 py-3 rounded-2xl border border-black/12 bg-white text-sm outline-none focus:border-[#1FA868]" />
      </Field>
      <Field lbl="Music / Audio title (optional)">
        <input value={form.music_title} onChange={(e) => setForm({ ...form, music_title: e.target.value })}
          placeholder="Original Audio · Acme Inc"
          className="w-full px-4 py-3 rounded-2xl border border-black/12 bg-white text-sm outline-none focus:border-[#1FA868]" />
      </Field>
      <Field lbl="Visibility">
        <select value={form.visibility} onChange={(e) => setForm({ ...form, visibility: e.target.value })}
          data-testid="m-reel-visibility"
          className="w-full px-4 py-3.5 rounded-2xl border border-black/12 bg-white text-sm outline-none focus:border-[#1FA868]">
          <option value="public">Public</option>
          <option value="followers">Followers only</option>
          <option value="private">Private</option>
        </select>
      </Field>

      <Field lbl="Tag products (optional)">
        <div className="max-h-44 overflow-y-auto bg-white rounded-2xl border border-black/8 divide-y divide-black/6">
          {products.slice(0, 30).map((p) => {
            const on = selectedProducts.includes(p.id);
            return (
              <button key={p.id} onClick={() => setSelectedProducts((s) => on ? s.filter((x) => x !== p.id) : [...s, p.id])}
                className={`w-full flex items-center gap-2.5 px-4 py-3 text-left ${on ? "bg-emerald-50/60" : ""}`}>
                <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center ${on ? "bg-[#1FA868] border-[#1FA868] text-white" : "border-black/20"}`}>
                  {on && <Check size={11} strokeWidth={3} />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-sm text-black truncate">{p.name}</div>
                  <div className="text-[11px] text-black/55">₦{Number(p.price || 0).toLocaleString()}</div>
                </div>
              </button>
            );
          })}
        </div>
      </Field>

      <button onClick={submit} disabled={busy || !videoUrl} data-testid="m-reel-publish"
        className="w-full py-3.5 rounded-2xl bg-gradient-to-r from-[#1FA868] to-[#16834D] text-white font-bold inline-flex items-center justify-center gap-2 shadow-lg shadow-[#1FA868]/30 disabled:opacity-60">
        <Sparkles size={15} /> {busy ? "Publishing…" : "Publish reel"}
      </button>
    </div>
  );
}

// ───────────────────── MY REELS — tenant manage own reels (edit + delete) ─────────────────────
function MyReelsPage() {
  const { subdomain } = useParams();
  const [reels, setReels] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState({ caption: "", hashtags: "" });
  const [playing, setPlaying] = useState(null);   // reel obj to play in modal
  const [deleting, setDeleting] = useState(null); // reel obj awaiting confirm

  const load = () => {
    setReels(null);
    axios.get(`${API}/t/reels`, { headers: { Authorization: `Bearer ${getToken()}` } })
      .then((r) => setReels(r.data || []))
      .catch(() => setReels([]));
  };
  useEffect(load, []);

  const startEdit = (r) => {
    setEditingId(r.id);
    setDraft({ caption: r.caption || "", hashtags: (r.hashtags || []).join(", ") });
  };
  const saveEdit = async () => {
    try {
      await axios.patch(`${API}/t/reels/${editingId}`, {
        video_url: reels.find((r) => r.id === editingId)?.video_url || "",
        caption: draft.caption,
        hashtags: draft.hashtags.split(",").map((s) => s.trim().replace(/^#/, "")).filter(Boolean),
      }, { headers: { Authorization: `Bearer ${getToken()}` } });
      toast.success("Reel updated");
      setEditingId(null);
      load();
    } catch (e) { toast.error("Failed to update"); }
  };
  const confirmDelete = async () => {
    if (!deleting) return;
    try {
      await axios.delete(`${API}/t/reels/${deleting.id}`, { headers: { Authorization: `Bearer ${getToken()}` } });
      toast.success("Reel deleted");
      setDeleting(null);
      load();
    } catch { toast.error("Failed"); }
  };

  return (
    <div data-testid="m-my-reels" className="px-4 py-5 pb-24 space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="font-display font-extrabold text-2xl text-black tracking-tight">My Reels</h1>
        <Link to={`/m/${subdomain}/reels/upload`} data-testid="m-my-reels-upload"
          className="px-3 py-1.5 rounded-xl bg-[#1FA868] text-white text-xs font-bold inline-flex items-center gap-1">
          <Upload size={12} /> Upload
        </Link>
      </div>

      {reels === null && <div className="text-center py-12 text-black/45">Loading…</div>}
      {reels !== null && reels.length === 0 && (
        <div className="bg-white rounded-2xl border border-dashed border-black/12 p-10 text-center">
          <Film size={32} className="mx-auto text-black/25 mb-2" />
          <div className="font-bold text-black">No reels yet</div>
          <div className="text-xs text-black/55 mt-1">Tap Upload to publish your first reel.</div>
        </div>
      )}

      <div className="grid grid-cols-2 gap-3">
        {(reels || []).map((r) => (
          <div key={r.id} data-testid={`m-my-reel-${r.id}`}
            className="bg-white rounded-2xl border border-black/8 overflow-hidden">
            <button onClick={() => setPlaying(r)} data-testid={`m-my-reel-play-${r.id}`}
              className="block w-full aspect-[9/16] bg-black relative active:scale-[0.98] transition-transform">
              {r.thumbnail_url ? (
                <img src={`${process.env.REACT_APP_BACKEND_URL}${r.thumbnail_url}`} alt="" className="w-full h-full object-cover" />
              ) : (
                <video src={`${process.env.REACT_APP_BACKEND_URL}${r.video_url}`} className="w-full h-full object-cover" muted preload="metadata" />
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-black/65 to-transparent" />
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className="w-12 h-12 rounded-full bg-black/55 backdrop-blur flex items-center justify-center opacity-90">
                  <Play size={22} className="text-white ml-0.5" fill="white" />
                </div>
              </div>
              <div className="absolute bottom-2 left-2 right-2 text-white text-left">
                <div className="text-[11px] font-bold line-clamp-2 drop-shadow">{r.caption || "—"}</div>
                <div className="text-[10px] opacity-90 mt-0.5">{r.view_count || 0} views · {r.like_count || 0} likes</div>
              </div>
              {r.moderation_status === "rejected" && (
                <span className="absolute top-1.5 left-1.5 text-[9px] font-bold px-1.5 py-0.5 rounded bg-rose-500 text-white">UNPUBLISHED</span>
              )}
            </button>
            <div className="grid grid-cols-2 gap-1.5 p-2">
              <button onClick={() => startEdit(r)} data-testid={`m-my-reel-edit-${r.id}`}
                className="px-2 py-1.5 rounded-lg bg-black/5 text-black text-[11px] font-bold inline-flex items-center justify-center gap-1">
                <Edit3 size={11} /> Edit
              </button>
              <button onClick={() => setDeleting(r)} data-testid={`m-my-reel-delete-${r.id}`}
                className="px-2 py-1.5 rounded-lg bg-rose-50 text-rose-700 text-[11px] font-bold inline-flex items-center justify-center gap-1">
                <Trash2 size={11} /> Delete
              </button>
            </div>
          </div>
        ))}
      </div>

      {editingId && (
        <div className="fixed inset-0 z-50 bg-black/55 flex items-end" onClick={() => setEditingId(null)}>
          <div className="bg-white w-full rounded-t-3xl p-5 space-y-3" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <div className="font-display font-bold text-lg text-black">Edit reel</div>
              <button onClick={() => setEditingId(null)} className="p-1.5 rounded-lg hover:bg-black/5"><X size={16} /></button>
            </div>
            <label className="block">
              <span className="block text-[11px] font-bold uppercase tracking-wider text-black/55 mb-1">Caption</span>
              <textarea value={draft.caption} onChange={(e) => setDraft({ ...draft, caption: e.target.value })}
                rows={3} data-testid="m-my-reel-caption"
                className="w-full px-3 py-2.5 rounded-xl border border-black/12 text-sm outline-none focus:border-[#1FA868]" />
            </label>
            <label className="block">
              <span className="block text-[11px] font-bold uppercase tracking-wider text-black/55 mb-1">Hashtags (comma-separated)</span>
              <input value={draft.hashtags} onChange={(e) => setDraft({ ...draft, hashtags: e.target.value })}
                data-testid="m-my-reel-hashtags"
                className="w-full px-3 py-2.5 rounded-xl border border-black/12 text-sm outline-none focus:border-[#1FA868]" />
            </label>
            <button onClick={saveEdit} data-testid="m-my-reel-save"
              className="w-full py-3 rounded-xl bg-gradient-to-r from-[#1FA868] to-[#16834D] text-white font-bold text-sm">Save changes</button>
          </div>
        </div>
      )}

      {playing && <ReelPlayerModal reel={playing} onClose={() => setPlaying(null)} />}

      {deleting && (
        <ConfirmDialog
          icon={Trash2}
          tint="rose"
          title="Delete this reel?"
          message={`"${(deleting.caption || "Untitled").slice(0, 80)}"`}
          description="This will permanently remove the reel, its views, likes and comments. This action cannot be undone."
          cancelLabel="Keep reel"
          confirmLabel="Delete reel"
          onCancel={() => setDeleting(null)}
          onConfirm={confirmDelete}
        />
      )}
    </div>
  );
}

// Reusable styled confirm dialog (replaces window.confirm)
function ConfirmDialog({ icon: Icon = AlertCircle, tint = "rose", title, message, description, cancelLabel = "Cancel", confirmLabel = "Confirm", onCancel, onConfirm }) {
  const tintMap = {
    rose: { bg: "bg-rose-50", text: "text-rose-600", btn: "bg-rose-600 hover:bg-rose-700" },
    amber: { bg: "bg-amber-50", text: "text-amber-600", btn: "bg-amber-600 hover:bg-amber-700" },
    emerald: { bg: "bg-emerald-50", text: "text-[#1FA868]", btn: "bg-[#1FA868] hover:bg-[#16834D]" },
  };
  const t = tintMap[tint] || tintMap.rose;
  return (
    <div data-testid="m-confirm-dialog" className="fixed inset-0 z-[60] bg-black/55 flex items-center justify-center p-4" onClick={onCancel}>
      <div className="bg-white rounded-3xl max-w-sm w-full p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className={`w-14 h-14 rounded-2xl ${t.bg} ${t.text} flex items-center justify-center mx-auto`}>
          <Icon size={26} strokeWidth={2.2} />
        </div>
        <div className="mt-4 text-center">
          <div className="font-display font-extrabold text-xl text-black">{title}</div>
          {message && <div className="mt-1.5 text-sm font-semibold text-black/80 truncate">{message}</div>}
          {description && <div className="mt-2 text-xs text-black/55 leading-relaxed">{description}</div>}
        </div>
        <div className="mt-6 flex gap-2">
          <button onClick={onCancel} data-testid="m-confirm-cancel"
            className="flex-1 py-3 rounded-2xl bg-black/5 text-black font-bold text-sm hover:bg-black/10">{cancelLabel}</button>
          <button onClick={onConfirm} data-testid="m-confirm-ok"
            className={`flex-1 py-3 rounded-2xl ${t.btn} text-white font-bold text-sm`}>{confirmLabel}</button>
        </div>
      </div>
    </div>
  );
}

// Full-screen vertical player for inline reel preview (used from My Reels grid)
function ReelPlayerModal({ reel, onClose }) {
  return (
    <div data-testid="m-reel-player" className="fixed inset-0 z-[55] bg-black flex items-center justify-center" onClick={onClose}>
      <button onClick={onClose} className="absolute top-4 right-4 z-10 w-10 h-10 rounded-full bg-white/15 backdrop-blur text-white flex items-center justify-center">
        <X size={18} />
      </button>
      <div className="absolute top-4 left-4 right-16 z-10 text-white">
        <div className="text-[10px] uppercase tracking-wider opacity-70 font-bold">Preview</div>
        <div className="font-bold text-sm line-clamp-1">{reel.caption || "—"}</div>
      </div>
      <video src={`${process.env.REACT_APP_BACKEND_URL}${reel.video_url}`}
        poster={reel.thumbnail_url ? `${process.env.REACT_APP_BACKEND_URL}${reel.thumbnail_url}` : undefined}
        controls autoPlay playsInline
        className="max-w-full max-h-full object-contain"
        onClick={(e) => e.stopPropagation()} />
    </div>
  );
}

// ───────────────────── GO LIVE — record up to 90s and publish as a reel ─────────────────────
function GoLivePage() {
  const { subdomain } = useParams();
  const nav = useNavigate();
  const videoRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const [stream, setStream] = useState(null);
  const [recording, setRecording] = useState(false);
  const [elapsed, setElapsed] = useState(0); // seconds
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewBlob, setPreviewBlob] = useState(null);
  const [caption, setCaption] = useState("");
  const [publishing, setPublishing] = useState(false);
  const MAX = 90;

  // Initialize camera
  useEffect(() => {
    let s;
    (async () => {
      try {
        s = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: "user", width: { ideal: 720 }, height: { ideal: 1280 } },
          audio: true,
        });
        setStream(s);
        if (videoRef.current) {
          videoRef.current.srcObject = s;
          videoRef.current.muted = true;
          videoRef.current.play().catch(() => {});
        }
      } catch (e) {
        toast.error("Camera access denied. Allow camera & microphone permissions to go live.");
      }
    })();
    return () => { if (s) s.getTracks().forEach((t) => t.stop()); };
  }, []);

  // Recording timer + auto-stop at MAX
  useEffect(() => {
    if (!recording) return;
    const t = setInterval(() => {
      setElapsed((s) => {
        if (s + 1 >= MAX) { stopRec(); return MAX; }
        return s + 1;
      });
    }, 1000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [recording]);

  const startRec = () => {
    if (!stream) return;
    chunksRef.current = [];
    let mime = "video/webm;codecs=vp9,opus";
    if (!window.MediaRecorder?.isTypeSupported?.(mime)) mime = "video/webm;codecs=vp8,opus";
    if (!window.MediaRecorder?.isTypeSupported?.(mime)) mime = "video/webm";
    try {
      const rec = new MediaRecorder(stream, { mimeType: mime });
      rec.ondataavailable = (e) => { if (e.data?.size > 0) chunksRef.current.push(e.data); };
      rec.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: mime });
        setPreviewBlob(blob);
        setPreviewUrl(URL.createObjectURL(blob));
      };
      rec.start(500);
      recorderRef.current = rec;
      setElapsed(0);
      setRecording(true);
    } catch (e) {
      toast.error("Recording not supported on this device.");
    }
  };

  const stopRec = () => {
    if (recorderRef.current?.state === "recording") recorderRef.current.stop();
    setRecording(false);
  };

  const publish = async () => {
    if (!previewBlob) return;
    setPublishing(true);
    try {
      const fd = new FormData();
      fd.append("file", previewBlob, `live_${Date.now()}.webm`);
      const upload = await axios.post(`${API}/t/reels/upload-video`, fd, {
        headers: { Authorization: `Bearer ${getToken()}`, "Content-Type": "multipart/form-data" },
      });
      await axios.post(`${API}/t/reels`, {
        video_url: upload.data.url,
        caption: caption || "Live moment",
        hashtags: ["live"],
        duration: elapsed,
        music_title: "",
      }, { headers: { Authorization: `Bearer ${getToken()}` } });
      toast.success("Live reel published!");
      nav(`/m/${subdomain}/reels`, { replace: true });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to publish live reel");
    } finally { setPublishing(false); }
  };

  const reset = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    setPreviewBlob(null);
    setElapsed(0);
  };

  const mins = Math.floor(elapsed / 60);
  const secs = (elapsed % 60).toString().padStart(2, "0");
  const pct = (elapsed / MAX) * 100;

  return (
    <div data-testid="m-go-live" className="fixed inset-0 z-50 bg-black text-white flex flex-col">
      <div className="flex items-center justify-between p-4">
        <button onClick={() => nav(-1)} className="p-2 rounded-xl bg-white/10 backdrop-blur"><ArrowLeft size={18} /></button>
        <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-rose-500/90 text-xs font-bold">
          <span className="w-2 h-2 rounded-full bg-white animate-pulse" />
          LIVE · max 90s
        </div>
        <div className="w-9" />
      </div>

      <div className="flex-1 relative overflow-hidden">
        {previewUrl ? (
          <video src={previewUrl} controls autoPlay loop playsInline className="w-full h-full object-cover" />
        ) : (
          <video ref={videoRef} playsInline muted className="w-full h-full object-cover scale-x-[-1]" />
        )}
        {/* Progress bar */}
        {recording && (
          <div className="absolute top-0 left-0 right-0 h-1 bg-white/15">
            <div className="h-full bg-rose-500 transition-all" style={{ width: `${pct}%` }} />
          </div>
        )}
        {/* Timer */}
        <div className="absolute top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-black/55 backdrop-blur text-xs font-mono font-bold">
          {mins}:{secs} / 1:30
        </div>
      </div>

      <div className="p-5 bg-black/85 backdrop-blur space-y-4">
        {previewUrl ? (
          <>
            <input value={caption} onChange={(e) => setCaption(e.target.value)} maxLength={150}
              data-testid="m-go-live-caption"
              placeholder="Add a caption…"
              className="w-full px-4 py-3 rounded-xl bg-white/10 border border-white/15 text-sm outline-none focus:border-[#1FA868] placeholder-white/45" />
            <div className="flex gap-2">
              <button onClick={reset} data-testid="m-go-live-retry"
                className="flex-1 py-3 rounded-xl bg-white/10 border border-white/15 font-bold text-sm">Retry</button>
              <button onClick={publish} disabled={publishing} data-testid="m-go-live-publish"
                className="flex-1 py-3 rounded-xl bg-gradient-to-r from-rose-500 to-rose-600 font-bold text-sm inline-flex items-center justify-center gap-1.5 disabled:opacity-60">
                {publishing ? "Publishing…" : <>Publish <Send size={13} /></>}
              </button>
            </div>
          </>
        ) : (
          <div className="flex items-center justify-center">
            {!recording ? (
              <button onClick={startRec} disabled={!stream} data-testid="m-go-live-start"
                className="w-20 h-20 rounded-full border-4 border-white flex items-center justify-center active:scale-95 disabled:opacity-50">
                <div className="w-14 h-14 rounded-full bg-rose-500" />
              </button>
            ) : (
              <button onClick={stopRec} data-testid="m-go-live-stop"
                className="w-20 h-20 rounded-full border-4 border-white flex items-center justify-center active:scale-95">
                <div className="w-10 h-10 rounded-md bg-rose-500" />
              </button>
            )}
          </div>
        )}
        <p className="text-[11px] text-white/55 text-center">Live captures up to 90 seconds, then publishes as a Reel to your followers.</p>
      </div>
    </div>
  );
}

// ───────────────────── MARKETPLACE BROWSE ─────────────────────
function MarketplaceBrowse() {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("trending");
  const { subdomain } = useParams();
  const reload = () => {
    setData(null);
    const params = new URLSearchParams(); if (q) params.append("q", q); if (sort) params.append("sort", sort);
    axios.get(`${API}/public/marketplace?${params.toString()}`).then((r) => setData(r.data)).catch(() => setData({ all: [] }));
  };
  useEffect(reload, [sort]); // eslint-disable-line
  useEffect(() => {
    const t = setTimeout(reload, 300); return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [q]);

  return (
    <div data-testid="m-marketplace-browse" className="px-4 py-5 pb-24 space-y-5">
      <div className="flex items-center justify-between">
        <h1 className="font-display font-extrabold text-2xl text-black tracking-tight">Marketplace</h1>
        {subdomain !== "guest" && (
          <Link to={`/m/${subdomain}/marketplace/listings`} data-testid="m-marketplace-manage"
            className="text-xs font-bold text-[#1FA868] inline-flex items-center gap-1">Manage <ChevronRight size={13} /></Link>
        )}
      </div>

      <div className="relative">
        <Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-black/35" />
        <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="m-marketplace-search"
          placeholder="Search products, stores…"
          className="w-full pl-10 pr-3.5 py-3 rounded-2xl border border-black/10 bg-white text-sm focus:border-[#1FA868] outline-none" />
      </div>

      <div className="flex gap-1.5 overflow-x-auto pb-1 -mx-4 px-4">
        {[["trending", "Trending"], ["newest", "Newest"], ["price_low", "₦ Low→High"], ["price_high", "₦ High→Low"]].map(([k, lbl]) => (
          <button key={k} onClick={() => setSort(k)} data-testid={`m-marketplace-sort-${k}`}
            className={`px-3.5 py-1.5 rounded-full text-xs font-bold whitespace-nowrap ${sort === k ? "bg-[#1B4332] text-white" : "bg-white text-black/65 border border-black/10"}`}>
            {lbl}
          </button>
        ))}
      </div>

      {data === null && <ListSkeleton />}

      {data?.promoted?.length > 0 && (
        <Section title="✨ Promoted">
          <div className="flex gap-3 overflow-x-auto pb-2 -mx-4 px-4">
            {data.promoted.map((l) => <ProductMiniCard key={l.id} l={l} />)}
          </div>
        </Section>
      )}

      {data?.top_stores?.length > 0 && (
        <Section title="🏪 Top stores">
          <div className="flex gap-3 overflow-x-auto pb-2 -mx-4 px-4">
            {data.top_stores.map((s) => (
              <Link key={s.subdomain} to={`/store/${s.subdomain}`} target="_blank"
                className="flex flex-col items-center gap-1.5 min-w-[72px]">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#1FA868] to-[#16834D] text-white flex items-center justify-center font-extrabold text-xl">
                  {(s.name || "S")[0]}
                </div>
                <div className="text-[11px] font-bold text-black text-center truncate w-16">{s.name}</div>
                <div className="text-[10px] text-black/55">{s.listings} items</div>
              </Link>
            ))}
          </div>
        </Section>
      )}

      <Section title="🔥 Trending">
        <div className="grid grid-cols-2 gap-3">
          {(data?.trending || []).map((l) => <ProductCard key={l.id} l={l} />)}
        </div>
      </Section>

      {data?.new_arrivals?.length > 0 && (
        <Section title="🆕 New arrivals">
          <div className="grid grid-cols-2 gap-3">
            {data.new_arrivals.map((l) => <ProductCard key={l.id} l={l} />)}
          </div>
        </Section>
      )}

      {data?.all?.length === 0 && (
        <div className="bg-white rounded-3xl border border-dashed border-black/12 p-9 text-center">
          <Store size={32} className="mx-auto text-black/25 mb-2" />
          <div className="font-bold text-black">No listings yet</div>
          <div className="text-xs text-black/55 mt-1">Tenants can publish products here once they have the Marketplace plan.</div>
        </div>
      )}
    </div>
  );
}

function ProductCard({ l }) {
  return (
    <Link to={`/store/${l.tenant_subdomain}`} target="_blank"
      className="bg-white rounded-2xl border border-black/8 overflow-hidden active:scale-[0.98] transition-transform">
      <div className="aspect-square bg-[#FAFBFB] flex items-center justify-center relative">
        {l.image_url ? (
          <img src={`${process.env.REACT_APP_BACKEND_URL}${l.image_url}`} alt="" className="w-full h-full object-cover" />
        ) : (
          <Package size={32} className="text-black/15" />
        )}
        {l.promoted && (
          <span className="absolute top-2 left-2 px-1.5 py-0.5 rounded bg-amber-400 text-amber-900 text-[9px] font-extrabold uppercase">Promoted</span>
        )}
      </div>
      <div className="p-2.5">
        <div className="font-bold text-xs text-black truncate">{l.title}</div>
        <div className="text-[10px] text-black/55 truncate">{l.tenant_name}</div>
        <div className="mt-1 font-extrabold text-sm text-[#1FA868]">₦{Number(l.price || 0).toLocaleString()}</div>
      </div>
    </Link>
  );
}

function ProductMiniCard({ l }) {
  return (
    <Link to={`/store/${l.tenant_subdomain}`} target="_blank"
      className="bg-white rounded-2xl border border-black/8 min-w-[150px] overflow-hidden">
      <div className="aspect-square bg-[#FAFBFB] flex items-center justify-center">
        {l.image_url ? <img src={`${process.env.REACT_APP_BACKEND_URL}${l.image_url}`} alt="" className="w-full h-full object-cover" /> : <Package size={24} className="text-black/15" />}
      </div>
      <div className="p-2.5">
        <div className="font-bold text-xs text-black truncate">{l.title}</div>
        <div className="mt-0.5 font-extrabold text-sm text-[#1FA868]">₦{Number(l.price || 0).toLocaleString()}</div>
      </div>
    </Link>
  );
}

// ───────────────────── TENANT MARKETPLACE LISTINGS ─────────────────────
function TenantListingsPage() {
  const { isLocked } = useEntitlements();
  const locked = isLocked("Marketplace");
  const m = { lbl: "Marketplace", icon: Store };
  const [listings, setListings] = useState(null);
  const [products, setProducts] = useState([]);
  const [showAdd, setShowAdd] = useState(false);

  const reload = () => {
    if (locked) return;
    setListings(null);
    axios.get(`${API}/t/marketplace/listings`, { headers: { Authorization: `Bearer ${getToken()}` } })
      .then((r) => setListings(r.data || [])).catch(() => setListings([]));
    axios.get(`${API}/t/products`, { headers: { Authorization: `Bearer ${getToken()}` } })
      .then((r) => setProducts(r.data || [])).catch(() => {});
  };
  useEffect(reload, [locked]); // eslint-disable-line

  if (locked) return <LockedView m={m} />;

  const addListing = async (productId) => {
    try {
      await axios.post(`${API}/t/marketplace/listings`, { product_id: productId },
        { headers: { Authorization: `Bearer ${getToken()}` } });
      toast.success("Listed in marketplace");
      setShowAdd(false);
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const remove = async (lid) => {
    try {
      await axios.delete(`${API}/t/marketplace/listings/${lid}`,
        { headers: { Authorization: `Bearer ${getToken()}` } });
      toast.success("Unlisted");
      reload();
    } catch { toast.error("Failed"); }
  };
  const togglePromote = async (l) => {
    try {
      await axios.patch(`${API}/t/marketplace/listings/${l.id}`,
        { product_id: l.product_id, promoted: !l.promoted },
        { headers: { Authorization: `Bearer ${getToken()}` } });
      reload();
    } catch { toast.error("Failed"); }
  };

  const listedIds = new Set((listings || []).map((l) => l.product_id));
  const availableProducts = products.filter((p) => !listedIds.has(p.id));

  return (
    <div data-testid="m-tenant-listings" className="px-4 py-5 pb-24 space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="font-display font-extrabold text-2xl text-black tracking-tight">My Listings</h1>
        <button onClick={() => setShowAdd(true)} data-testid="m-listings-add"
          className="px-3 py-1.5 rounded-full bg-[#1FA868] text-white text-xs font-bold inline-flex items-center gap-1">
          <Plus size={12} /> List product
        </button>
      </div>
      {listings === null && <ListSkeleton />}
      {listings?.length === 0 && (
        <div className="bg-white rounded-3xl border border-dashed border-black/12 p-9 text-center">
          <Store size={28} className="mx-auto text-black/25 mb-2" />
          <div className="font-bold text-black">No products listed yet</div>
          <div className="text-xs text-black/55 mt-1">Tap "List product" to publish to the marketplace.</div>
        </div>
      )}
      {listings && listings.map((l) => (
        <div key={l.id} className="bg-white rounded-2xl border border-black/8 p-3 flex items-center gap-3">
          <div className="w-14 h-14 rounded-xl bg-[#FAFBFB] flex items-center justify-center overflow-hidden flex-shrink-0">
            {l.product?.image_url ? <img src={`${process.env.REACT_APP_BACKEND_URL}${l.product.image_url}`} alt="" className="w-full h-full object-cover" /> : <Package size={20} className="text-black/20" />}
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-bold text-sm text-black truncate">{l.title}</div>
            <div className="text-[11px] text-black/55">₦{Number(l.product?.price || 0).toLocaleString()} · {l.views} views</div>
            <span className={`mt-1 inline-block text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${l.status === "approved" ? "bg-emerald-50 text-emerald-700" : l.status === "rejected" ? "bg-rose-50 text-rose-700" : "bg-amber-50 text-amber-700"}`}>{l.status}</span>
          </div>
          <div className="flex flex-col gap-1">
            <button onClick={() => togglePromote(l)} className={`px-2 py-1 rounded-lg text-[10px] font-bold ${l.promoted ? "bg-amber-100 text-amber-800" : "bg-black/5 text-black/65"}`}>
              {l.promoted ? "Promoted ★" : "Promote"}
            </button>
            <button onClick={() => remove(l.id)} className="px-2 py-1 rounded-lg text-[10px] font-bold text-rose-600 active:bg-rose-50">Unlist</button>
          </div>
        </div>
      ))}

      {showAdd && (
        <div className="fixed inset-0 z-50 bg-black/55 flex items-end" onClick={() => setShowAdd(false)}>
          <div className="bg-white w-full max-h-[80vh] rounded-t-3xl p-4 flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between pb-2 border-b border-black/8">
              <div className="font-bold text-base">Choose a product</div>
              <button onClick={() => setShowAdd(false)} className="p-1.5 rounded-lg hover:bg-black/5"><X size={16} /></button>
            </div>
            <div className="flex-1 overflow-y-auto py-2 space-y-2">
              {availableProducts.length === 0 ? (
                <div className="text-center text-sm text-black/55 py-10">All your products are already listed.</div>
              ) : availableProducts.map((p) => (
                <button key={p.id} onClick={() => addListing(p.id)} data-testid={`m-list-product-${p.id}`}
                  className="w-full flex items-center gap-3 p-3 rounded-2xl border border-black/8 text-left active:bg-black/5">
                  <div className="w-11 h-11 rounded-xl bg-[#FAFBFB] flex items-center justify-center flex-shrink-0">
                    {p.image_url ? <img src={`${process.env.REACT_APP_BACKEND_URL}${p.image_url}`} alt="" className="w-full h-full object-cover rounded-xl" /> : <Package size={16} className="text-black/20" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-sm text-black truncate">{p.name}</div>
                    <div className="text-[11px] text-black/55">₦{Number(p.price || 0).toLocaleString()} · stock {p.stock || 0}</div>
                  </div>
                  <Plus size={14} className="text-[#1FA868]" />
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

